import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { attendanceAPI, batchesAPI, tutorsAPI, studentsAPI } from '../utils/api';
import { generateAttendanceCSV, generateAttendancePDF } from '../utils/exportUtils';
import { tField } from '../utils/bilingualHelper';
import { ATTENDANCE_COLORS, getAttendanceBadgeClass } from '../utils/attendanceTheme';

/* ─── small helpers ─── */
const FMT_DATE = d => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const FMT_TIME = d => new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const STATUS_BADGE = getAttendanceBadgeClass;

const getSafeName = (nameObj) => {
  return tField(nameObj) || 'Unknown';
};

// Returns "YYYY-MM" for the current month or offset months
function monthKey(offsetMonths = 0) {
  const d = new Date(); 
  d.setMonth(d.getMonth() + offsetMonths);   
  return d.toISOString().slice(0, 7);
}

function monthLabel(key) {
  const [y, m] = key.split('-');
  return new Date(y, m - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
}

/* ─── Monthly drill-down modal ─── */
function MonthlyDrillModal({ student, month, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDrillBatch, setSelectedDrillBatch] = useState(null);

  useEffect(() => {
    setLoading(true);
    attendanceAPI.getStudentMonthly(student._id, month)
      .then(r => {
        setData(r.data);
        if (r.data && r.data.batches && r.data.batches.length > 0) {
          setSelectedDrillBatch(r.data.batches[0].batchId);
        }
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [student._id, month]);

  const getMonthRecords = () => {
    if (!data || !data.batches) return [];
    const records = [];
    const batch = data.batches.find(b => b.batchId === selectedDrillBatch);
    if (batch) {
      batch.records.forEach(r => {
        records.push({ Date: FMT_DATE(r.date), Batch: batch.batchName, Status: r.status, Note: r.note || '', MarkedBy: r.markedBy });
      });
    }
    return records;
  };

  const handleDownloadMonth = (format) => {
    const batch = data.batches.find(b => b.batchId === selectedDrillBatch);
    if (!batch) return;
    
    const records = getMonthRecords();
    const stats = {
      scheduled: batch.scheduledClasses,
      present: batch.present,
      late: batch.late,
      absent: batch.absent,
      leave: batch.leave || 0,
      makeup: batch.makeup || 0
    };

    if (format === 'csv') {
      generateAttendanceCSV(records, `${getSafeName(student.name)}_${month}_Attendance`, stats);
    } else {
      generateAttendancePDF(records, `${getSafeName(student.name)}_${month}_Attendance`, getSafeName(student.name), monthLabel(month), stats);
    }
  };


  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: '20px', width: '90vw', height: '90vh', maxWidth: '1200px',
        overflowY: 'auto', padding: '28px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #f0f0f0' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: '#1a1a2e' }}>{getSafeName(student.name)}</h2>
            <p style={{ margin: '4px 0 0', color: '#888', fontSize: '15px' }}>{monthLabel(month)} — Attendance Detail</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '28px', color: '#999', padding: '4px' }}>✕</button>
        </div>

        {/* Batch Selector Tabs */}
        {data && data.batches && data.batches.length > 1 && (
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', overflowX: 'auto', paddingBottom: '8px' }}>
            {data.batches.map(b => (
              <button
                key={b.batchId}
                onClick={() => setSelectedDrillBatch(b.batchId)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '24px',
                  border: '1px solid #7b1d3a',
                  background: selectedDrillBatch === b.batchId ? '#7b1d3a' : '#fff',
                  color: selectedDrillBatch === b.batchId ? '#fff' : '#7b1d3a',
                  fontWeight: 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s'
                }}
              >
                {getSafeName(b.batchName)}
              </button>
            ))}
          </div>
        )}

        {/* Download Buttons - Compact Row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fcfaf7', border: '1px solid #e2d8ce', padding: '10px 16px', borderRadius: '10px', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#7a7068', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Export Report</div>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#888', fontWeight: 500 }}>{monthLabel(month)}:</span>
              <button onClick={() => handleDownloadMonth('csv')} style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', color: '#333' }}>CSV</button>
              <button onClick={() => handleDownloadMonth('pdf')} style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', color: '#7b1d3a' }}>PDF</button>
            </div>
          </div>
        </div>

        {loading && <div style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>⏳ Loading...</div>}

        {!loading && !data && <div style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>Could not load data.</div>}

        {!loading && data && data.batches.filter(b => b.batchId === selectedDrillBatch).map(batch => (
          <div key={batch.batchId} style={{ marginBottom: '24px' }}>
            {/* Batch header */}
            <div style={{
              background: 'linear-gradient(135deg, #7b1d3a 0%, #c04060 100%)',
              borderRadius: '14px', padding: '14px 18px', marginBottom: '12px', color: '#fff',
            }}>
              <div style={{ fontWeight: 700, fontSize: '16px' }}>{getSafeName(batch.batchName)}</div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>{batch.level} · {(batch.days || []).join(', ')}</div>
            </div>

            {/* Stat pills */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '8px', marginBottom: '14px' }}>
              {[
                { label: 'Scheduled', val: batch.scheduledClasses, color: '#6c757d' },
                { label: 'Present',   val: batch.present,          color: ATTENDANCE_COLORS.present },
                { label: 'Late',      val: batch.late,             color: ATTENDANCE_COLORS.late },
                { label: 'Absent',    val: batch.absent,           color: ATTENDANCE_COLORS.absent },
                { label: 'Leave',     val: batch.leave || 0,       color: ATTENDANCE_COLORS.leave },
                { label: 'Make-up',   val: batch.makeup || 0,      color: ATTENDANCE_COLORS.makeup },
              ].map(item => (
                <div key={item.label} style={{
                  background: '#f8f9fa', borderRadius: '10px', padding: '10px 6px', textAlign: 'center',
                  border: `1px solid ${item.color}22`,
                }}>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: item.color }}>{item.val}</div>
                  <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{item.label}</div>
                </div>
              ))}
            </div>

            {/* Daily records */}
            {batch.records.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#bbb', padding: '12px', fontSize: '13px' }}>
                No records marked this month.
              </div>
            ) : (
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '400px' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      {['Date', 'Status', 'Note', 'Marked By'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#666', fontWeight: 600, borderBottom: '1px solid #eee' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {batch.records.map(rec => (
                      <tr key={rec._id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px', color: '#333' }}>{FMT_DATE(rec.date)}</td>
                        <td style={{ padding: '8px 12px' }}>
                          <span className={`badge ${STATUS_BADGE(rec.status)}`}>{rec.status}</span>
                        </td>
                        <td style={{ padding: '8px 12px', color: '#888' }}>{rec.note || '—'}</td>
                        <td style={{ padding: '8px 12px', color: '#555' }}>{rec.markedBy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Monthly Report Tab ─── */
function MonthlyReportTab({ batches, students, defaultBatchId }) {
  const { t } = useTranslation();
  const [selectedMonth, setSelectedMonth] = useState(monthKey(0));
  const [selectedBatch, setSelectedBatch] = useState(defaultBatchId || (batches && batches.length > 0 ? batches[0]._id : ''));
  
  useEffect(() => {
    if (defaultBatchId) {
      setSelectedBatch(defaultBatchId);
    } else if (!selectedBatch && batches && batches.length > 0) {
      setSelectedBatch(batches[0]._id);
    }
  }, [batches, selectedBatch, defaultBatchId]);
  const [monthlyData, setMonthlyData]     = useState([]); // per-student summaries
  const [loading, setLoading]             = useState(false);
  const [drillStudent, setDrillStudent]   = useState(null);
  const [months, setMonths]               = useState([]);

  // Generate available months based on selected batch duration
  useEffect(() => {
    let startD = new Date();
    startD.setMonth(startD.getMonth() - 5);
    let endD = new Date();

    if (selectedBatch) {
      const b = batches.find(x => x._id === selectedBatch);
      if (b && b.startDate) startD = new Date(b.startDate);
      if (b && b.endDate) endD = new Date(b.endDate);
    }
    
    if (startD > endD) {
      const temp = startD; startD = endD; endD = temp;
    }

    const mList = [];
    let curr = new Date(startD.getFullYear(), startD.getMonth(), 1);
    const end = new Date(endD.getFullYear(), endD.getMonth(), 1);
    
    let count = 0;
    while (curr <= end && count < 36) { // Max 3 years
      const yStr = curr.getFullYear();
      const mStr = String(curr.getMonth() + 1).padStart(2, '0');
      mList.push(`${yStr}-${mStr}`);
      curr.setMonth(curr.getMonth() + 1);
      count++;
    }
    mList.sort((a, b) => b.localeCompare(a)); // Descending
    
    if (mList.length === 0) {
      const today = new Date();
      mList.push(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
    }
    
    setMonths(mList);
    
    setMonthlyData(prev => {
      // Small trick to avoid resetting data if the month is valid
      if (!mList.includes(selectedMonth)) {
        setSelectedMonth(mList.includes(monthKey(0)) ? monthKey(0) : mList[0]);
      }
      return prev;
    });
  }, [selectedBatch, batches, selectedMonth]);

  // Students filtered by batch
  const filteredStudents = !selectedBatch
    ? []
    : students.filter(s => {
        const bid = selectedBatch;
        return s.batch?._id === bid || (s.batchIds || []).some(id => id === bid || id?._id === bid);
      });

  const loadMonthlyData = useCallback(async () => {
    if (filteredStudents.length === 0) { setMonthlyData([]); return; }
    setLoading(true);
    try {
      const results = await Promise.all(
        filteredStudents.map(s =>
          attendanceAPI.getStudentMonthly(s._id, selectedMonth)
            .then(r => ({ student: s, data: r.data }))
            .catch(() => ({ student: s, data: null }))
        )
      );

      // Flatten — one row per student, aggregate across batches (filtered by selectedBatch)
      const rows = results.map(({ student, data }) => {
        if (!data || !data.batches.length) {
          return { student, scheduled: 0, present: 0, late: 0, absent: 0, percentage: 0, batches: [] };
        }
        
        let targetBatches = data.batches;
        if (selectedBatch) {
          targetBatches = targetBatches.filter(b => b.batchId === selectedBatch);
        }

        const scheduled = targetBatches.reduce((a, b) => a + b.scheduledClasses, 0);
        const present   = targetBatches.reduce((a, b) => a + b.present, 0);
        const late      = targetBatches.reduce((a, b) => a + b.late, 0);
        const absent    = targetBatches.reduce((a, b) => a + b.absent, 0);
        const leave     = targetBatches.reduce((a, b) => a + (b.leave || 0), 0);
        const makeup    = targetBatches.reduce((a, b) => a + (b.makeup || 0), 0);
        const percentage = scheduled > 0 ? Math.round((present + late) / scheduled * 100) : 0;
        return { student, scheduled, present, late, absent, leave, makeup, percentage, batches: targetBatches };
      });

      // Sort by percentage asc (low attenders first)
      rows.sort((a, b) => a.percentage - b.percentage);
      setMonthlyData(rows);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filteredStudents.length, selectedMonth, selectedBatch]); // eslint-disable-line

  useEffect(() => { loadMonthlyData(); }, [loadMonthlyData]);

  const [downloadingBatch, setDownloadingBatch] = useState(false);

  const handleBatchDownloadCSV = async () => {
    if (!selectedBatch) return alert('Please select a specific batch first.');
    const batchObj = batches.find(b => b._id === selectedBatch);
    if (!batchObj) return;

    if (monthlyData.length === 0) return alert('No attendance records found for this period.');

    setDownloadingBatch(true);
    try {
      const headers = ['Student ID', 'Student Name', 'Scheduled', 'Present', 'Late', 'Absent', 'Leave', 'Make-up'];
      const csvRows = [
        `"Batch: ${getSafeName(batchObj.name)} (${batchObj.level}) - ${monthLabel(selectedMonth)} Attendance Matrix"`,
        headers.join(',')
      ];

      monthlyData.forEach(r => {
        const stId = r.student?.studentId || '-';
        const stName = getSafeName(r.student?.name) || '-';
        csvRows.push(`"${stId}","${stName}","${r.scheduled}","${r.present}","${r.late}","${r.absent}","${r.leave || 0}","${r.makeup || 0}"`);
      });

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${getSafeName(batchObj.name)}_${selectedMonth}_Matrix.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloadingBatch(false);
    }
  };

  const handleBatchDownloadPDF = async () => {
    if (!selectedBatch) return alert('Please select a specific batch first.');
    const batchObj = batches.find(b => b._id === selectedBatch);
    if (!batchObj) return;

    if (monthlyData.length === 0) return alert('No attendance records found for this period.');

    setDownloadingBatch(true);
    try {
      const printWindow = window.open('', '_blank');
      const html = `
        <html>
          <head>
            <title>${getSafeName(batchObj.name)} Attendance Matrix</title>
            <style>
              body { font-family: 'Arial', sans-serif; padding: 20px; color: #333; }
              h1 { color: #7b1d3a; margin-bottom: 5px; font-size: 24px; }
              table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f8f9fa; color: #444; font-size: 12px; }
              .info-grid { display: flex; gap: 40px; margin-bottom: 20px; padding: 15px; background: #fcfaf7; border-radius: 8px; border: 1px solid #e2d8ce; }
              .info-item { display: flex; flex-direction: column; }
              .info-label { font-size: 11px; font-weight: bold; color: #888; text-transform: uppercase; }
              .info-value { font-size: 14px; font-weight: bold; color: #333; }
            </style>
          </head>
          <body>
            <h1>Batch Attendance Matrix</h1>
            
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Batch Name</span>
                <span class="info-value">${getSafeName(batchObj.name)}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Level / Category</span>
                <span class="info-value">${batchObj.level || '-'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Report Period</span>
                <span class="info-value">${monthLabel(selectedMonth)}</span>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Student Name</th>
                  <th>Scheduled</th>
                  <th>Present</th>
                  <th>Late</th>
                  <th>Absent</th>
                  <th>Leave</th>
                  <th>Make-up</th>
                </tr>
              </thead>
              <tbody>
                ${monthlyData.map(r => {
                  const stId = r.student?.studentId || '-';
                  const stName = getSafeName(r.student?.name) || '-';
                  return `
                    <tr>
                      <td>${stId}</td>
                      <td>${stName}</td>
                      <td>${r.scheduled}</td>
                      <td><span style="color: #10b981; font-weight: bold;">${r.present}</span></td>
                      <td><span style="color: #f59e0b;">${r.late}</span></td>
                      <td><span style="color: #ef4444;">${r.absent}</span></td>
                      <td><span style="color: #1d4ed8; font-weight: bold;">${r.leave || 0}</span></td>
                      <td><span style="color: #7c3aed; font-weight: bold;">${r.makeup || 0}</span></td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
            <script>
              window.onload = () => { window.print(); };
            </script>
          </body>
        </html>
      `;
      printWindow.document.write(html);
      printWindow.document.close();
    } finally {
      setDownloadingBatch(false);
    }
  };
  const atRisk   = monthlyData.filter(r => r.scheduled > 0 && r.percentage < 75).length;
  const perfect  = monthlyData.filter(r => r.percentage === 100).length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h3 style={{ margin: 0, fontSize: '20px', color: '#1a1a2e', fontWeight: 700 }}>{t('attendanceUI.monthlyOverview')}</h3>
      </div>
      
      {/* Controls */}
      <div style={{ 
        background: '#fff', 
        borderRadius: '20px', 
        padding: '24px', 
        marginBottom: '24px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.04)',
        border: '1px solid #f0e6da'
      }}>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group mb-0" style={{ flex: '1 1 200px', maxWidth: '300px' }}>
            <label style={{ fontSize: '12px', fontWeight: 700, color: '#7a7068', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', display: 'block' }}>{t('attendanceUI.month')}</label>
            <select 
              style={{ 
                width: '100%', padding: '14px 16px', borderRadius: '12px', 
                border: '1px solid #e2d8ce', background: '#fcfaf7', 
                fontSize: '14px', fontWeight: 600, color: '#3d3730', outline: 'none' 
              }} 
              value={selectedMonth} 
              onChange={e => setSelectedMonth(e.target.value)}
            >
              {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
          </div>
          <div className="form-group mb-0" style={{ flex: '1 1 200px', maxWidth: '400px' }}>
            <label style={{ fontSize: '12px', fontWeight: 700, color: '#7a7068', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', display: 'block' }}>{t('attendanceUI.filterBatch')}</label>
            <select 
              style={{ 
                width: '100%', padding: '14px 16px', borderRadius: '12px', 
                border: '1px solid #e2d8ce', background: '#fcfaf7', 
                fontSize: '14px', fontWeight: 600, color: '#3d3730', outline: 'none' 
              }} 
              value={selectedBatch} 
              onChange={e => setSelectedBatch(e.target.value)}
            >
              {batches.map(b => <option key={b._id} value={b._id}>{getSafeName(b.name)} ({b.level})</option>)}
            </select>
          </div>
          
          {selectedBatch && (
            <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#7a7068', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', display: 'block' }}>{t('attendance.exportMatrix')}</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ display: 'flex', background: '#fcfaf7', border: '1px solid #e2d8ce', borderRadius: '12px', overflow: 'hidden' }}>
                  <button 
                    onClick={() => handleBatchDownloadCSV()}
                    disabled={downloadingBatch}
                    style={{ padding: '14px 16px', background: 'transparent', border: 'none', borderRight: '1px solid #e2d8ce', color: '#7b1d3a', fontWeight: 600, fontSize: '13px', cursor: downloadingBatch ? 'wait' : 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={e => !downloadingBatch && (e.currentTarget.style.background = '#f0e6da')}
                    onMouseLeave={e => !downloadingBatch && (e.currentTarget.style.background = 'transparent')}
                    title="Download Selected Month CSV"
                  >
                    {downloadingBatch ? '...' : 'Month CSV'}
                  </button>
                  <button 
                    onClick={() => handleBatchDownloadPDF()}
                    disabled={downloadingBatch}
                    style={{ padding: '14px 16px', background: 'transparent', border: 'none', color: '#7b1d3a', fontWeight: 600, fontSize: '13px', cursor: downloadingBatch ? 'wait' : 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={e => !downloadingBatch && (e.currentTarget.style.background = '#f0e6da')}
                    onMouseLeave={e => !downloadingBatch && (e.currentTarget.style.background = 'transparent')}
                    title="Download Selected Month PDF"
                  >
                    {downloadingBatch ? '...' : 'Month PDF'}
                  </button>
                </div>


              </div>
            </div>
          )}
        </div>
      </div>

      {/* Summary pills */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '24px' }}>
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '20px', padding: '24px', color: '#fff',
          boxShadow: '0 10px 25px -5px rgba(16, 185, 129, 0.4)', position: 'relative', overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', right: '-10px', top: '-10px', fontSize: '80px', opacity: 0.1 }}>👥</div>
          <div style={{ fontSize: '14px', fontWeight: 600, letterSpacing: '0.5px', opacity: 0.9, marginBottom: '8px', textTransform: 'uppercase' }}>{t('attendanceUI.totalEnrolled', 'Total Students')}</div>
          <div style={{ fontSize: '42px', fontWeight: 800, lineHeight: 1 }}>{monthlyData.length}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          borderRadius: '20px', padding: '24px', color: '#fff',
          boxShadow: '0 10px 25px -5px rgba(245, 158, 11, 0.4)', position: 'relative', overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', right: '-10px', top: '-10px', fontSize: '80px', opacity: 0.1 }}>⚠️</div>
          <div style={{ fontSize: '14px', fontWeight: 600, letterSpacing: '0.5px', opacity: 0.9, marginBottom: '8px', textTransform: 'uppercase' }}>{t('attendanceUI.atRisk')}</div>
          <div style={{ fontSize: '42px', fontWeight: 800, lineHeight: 1 }}>{atRisk}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          borderRadius: '20px', padding: '24px', color: '#fff',
          boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.4)', position: 'relative', overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', right: '-10px', top: '-10px', fontSize: '80px', opacity: 0.1 }}>🌟</div>
          <div style={{ fontSize: '14px', fontWeight: 600, letterSpacing: '0.5px', opacity: 0.9, marginBottom: '8px', textTransform: 'uppercase' }}>{t('attendanceUI.perfectAttendance')}</div>
          <div style={{ fontSize: '42px', fontWeight: 800, lineHeight: 1 }}>{perfect}</div>
        </div>
      </div>

      {/* Table */}
      <div className="dash-card" style={{ minHeight: '350px' }}>
        <div className="dash-card-header">
          <div className="dash-card-title">{t('attendanceUI.colStudent')} {t('attendance.title')} — {monthLabel(selectedMonth)}</div>
          <span className="badge badge-blue">{monthlyData.length} students</span>
        </div>

        {loading ? (
          <div className="dash-loading">⏳ {t('attendanceUI.analyzing')}</div>
        ) : monthlyData.length === 0 ? (
          <div className="dash-empty">
            <div className="dash-empty-icon">📅</div>
            <div className="dash-empty-title">{t('attendanceUI.noStudentsFound')}</div>
            <p className="dash-empty-sub">{t('attendanceUI.tryDifferentBatch')}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="dash-table" style={{ minWidth: '700px' }}>
              <thead>
                <tr>
                  <th>{t('attendanceUI.colStudent')}</th>
                  <th>{t('attendanceUI.colBatchTaught')}</th>
                  <th style={{ textAlign: 'center' }}>{t('attendanceUI.scheduled')}</th>
                  <th style={{ textAlign: 'center' }}>{t('attendanceUI.present')}</th>
                  <th style={{ textAlign: 'center' }}>{t('attendanceUI.late')}</th>
                  <th style={{ textAlign: 'center' }}>{t('attendanceUI.absent')}</th>
                  <th style={{ textAlign: 'center' }}>Leave</th>
                  <th style={{ textAlign: 'center' }}>Make-up</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map(row => (
                  <tr key={row.student._id} style={{ cursor: 'pointer' }} onClick={() => setDrillStudent(row.student)}>
                    <td>
                      <div style={{ fontWeight: 600, color: '#1a1a2e' }}>{getSafeName(row.student?.name)}</div>
                      <div style={{ fontSize: '12px', color: '#888' }}>{row.student.studentId || ''}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: '13px', color: '#555' }}>
                        {row.batches.map(b => getSafeName(b.batchName)).join(', ') || '—'}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center', color: '#555' }}>{row.scheduled}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: ATTENDANCE_COLORS.present, fontWeight: 600 }}>{row.present}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: ATTENDANCE_COLORS.late, fontWeight: 600 }}>{row.late}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: ATTENDANCE_COLORS.absent, fontWeight: 600 }}>{row.absent}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: ATTENDANCE_COLORS.leave, fontWeight: 600 }}>{row.leave || 0}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: ATTENDANCE_COLORS.makeup, fontWeight: 600 }}>{row.makeup || 0}</span>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline"
                        style={{ padding: '4px 12px', fontSize: '12px', borderRadius: '8px' }}
                        onClick={e => { e.stopPropagation(); setDrillStudent(row.student); }}
                      >
                        {t('attendanceUI.details')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drill-down modal */}
      {drillStudent && (
        <MonthlyDrillModal
          student={drillStudent}
          month={selectedMonth}
          onClose={() => setDrillStudent(null)}
        />
      )}
    </div>
  );
}


/* ════════════════════════════════════════
   Tutor Attendance Helpers & Hierarchical Hub
   Flow: All Tutors (Row by Row) -> Tutor's Batches (Row by Row) -> Batch Attendance Ledger
════════════════════════════════════════ */
function formatSessionDuration(start, end) {
  if (!start || !end) return '—';
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (isNaN(s) || isNaN(e) || e <= s) return '—';
  const diffMinutes = Math.round((e - s) / 60000);
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
}

function getTutorDisplay(record, tutors) {
  const tutorDoc = (typeof record.userId === 'object' && record.userId) ? record.userId : null;
  const tutorId = tutorDoc?._id || record.userId;
  const matched = (tutors || []).find(t => 
    String(t.userId?._id || t.userId) === String(tutorId) || 
    String(t._id) === String(tutorId)
  );
  const name = tField(matched?.name) || tField(tutorDoc?.name) || 'Faculty Member';
  const email = tutorDoc?.email || matched?.userId?.email || matched?.email || '';
  return { name, email };
}

// Calculate only the executing months for a specific batch (respects batch.startDate to batch.endDate + recorded sessions)
function getBatchExecutingMonths(batch, records = []) {
  if (!batch) return [];
  let startD = batch.startDate ? new Date(batch.startDate) : null;
  let endD = batch.endDate ? new Date(batch.endDate) : null;

  const list = [];

  if (startD || endD) {
    if (startD && !endD) {
      endD = new Date(startD);
      endD.setMonth(endD.getMonth() + 6);
    } else if (!startD && endD) {
      startD = new Date(endD);
      startD.setMonth(startD.getMonth() - 6);
    }

    if (startD > endD) {
      const temp = startD; startD = endD; endD = temp;
    }

    let curr = new Date(startD.getFullYear(), startD.getMonth(), 1);
    const end = new Date(endD.getFullYear(), endD.getMonth(), 1);

    let count = 0;
    while (curr <= end && count < 60) {
      const yStr = curr.getFullYear();
      const mStr = String(curr.getMonth() + 1).padStart(2, '0');
      list.push(`${yStr}-${mStr}`);
      curr.setMonth(curr.getMonth() + 1);
      count++;
    }
  }

  // Also include any months where attendance sessions were actually conducted
  (records || []).forEach(r => {
    const raw = r.date || r.createdAt;
    if (raw) {
      const ym = String(raw).slice(0, 7);
      if (/^\d{4}-\d{2}$/.test(ym) && !list.includes(ym)) {
        list.push(ym);
      }
    }
  });

  if (list.length === 0) {
    const today = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const yStr = d.getFullYear();
      const mStr = String(d.getMonth() + 1).padStart(2, '0');
      list.push(`${yStr}-${mStr}`);
    }
  }

  // Sort descending (latest executing month first)
  list.sort((a, b) => b.localeCompare(a));
  return list;
}

// Find all batches taught by a specific tutor
function getBatchesForTutor(tutor, allBatches = []) {
  if (!tutor) return [];
  const tutorId = String(tutor._id || '');
  const tutorUserId = String(tutor.userId?._id || tutor.userId || '');
  
  return (allBatches || []).filter(b => {
    // 1. Check batch.tutor
    const bTutorId = String(typeof b.tutor === 'object' && b.tutor !== null ? (b.tutor._id || '') : (b.tutor || ''));
    if (bTutorId && (bTutorId === tutorId || (tutorUserId && bTutorId === tutorUserId))) {
      return true;
    }
    // 2. Check tutor.activeBatchIds
    if (Array.isArray(tutor.activeBatchIds)) {
      const matchActive = tutor.activeBatchIds.some(ab => {
        const abId = String(typeof ab === 'object' && ab !== null ? (ab._id || '') : (ab || ''));
        return abId === String(b._id);
      });
      if (matchActive) return true;
    }
    return false;
  });
}

function TutorAttendanceHub({ batches = [], tutors = [] }) {
  const { t } = useTranslation();
  
  // Navigation / Selection States:
  // Step 1: All Tutors (selectedTutor == null)
  // Step 2: Tutor's Batches (selectedTutor != null, selectedBatch == null)
  // Step 3: Batch Attendance Ledger (selectedTutor != null, selectedBatch != null)
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);

  // Search & Filter States
  const [tutorSearch, setTutorSearch]     = useState('');
  const [viewMode, setViewMode]           = useState('all'); // 'all' | 'date' | 'month'
  const [selectedDate, setSelectedDate]   = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedStatus, setSelectedStatus] = useState('');

  // Attendance Records State
  const [allBatchSessions, setAllBatchSessions] = useState([]);
  const [records, setRecords]                   = useState([]);
  const [stats, setStats]                       = useState({ total: 0, present: 0, absent: 0, checkedOut: 0 });
  const [loading, setLoading]                   = useState(false);

  // Compute only the valid executing months for the selected batch + recorded class sessions
  const batchMonths = useMemo(() => {
    return getBatchExecutingMonths(selectedBatch, allBatchSessions);
  }, [selectedBatch, allBatchSessions]);

  // Synchronize selectedMonth and selectedDate when selectedBatch changes
  useEffect(() => {
    if (selectedBatch) {
      const bMonths = getBatchExecutingMonths(selectedBatch, allBatchSessions);
      if (bMonths.length > 0) {
        setSelectedMonth(prev => {
          // Preserve user-selected month if it is already within this batch's executing months
          if (prev && bMonths.includes(prev)) return prev;
          const todayMonth = new Date().toISOString().slice(0, 7);
          return bMonths.includes(todayMonth) ? todayMonth : bMonths[0];
        });
      }

      // If today is outside batch start/end, adjust selectedDate to fall within batch bounds
      const todayStr = new Date().toISOString().split('T')[0];
      const startKey = selectedBatch.startDate ? selectedBatch.startDate.slice(0, 10) : null;
      const endKey = selectedBatch.endDate ? selectedBatch.endDate.slice(0, 10) : null;
      if (startKey && todayStr < startKey) {
        setSelectedDate(startKey);
      } else if (endKey && todayStr > endKey) {
        setSelectedDate(endKey);
      }
    }
  }, [selectedBatch?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch Attendance when a Tutor and Batch are selected (unifies direct tutor check-ins & student roll-call marks)
  const fetchBatchTutorAttendance = useCallback(async () => {
    if (!selectedTutor || !selectedBatch) {
      setAllBatchSessions([]);
      setRecords([]);
      setStats({ total: 0, present: 0, absent: 0, checkedOut: 0 });
      return;
    }

    setLoading(true);
    try {
      // 1. Resolve clean, non-null Tutor ID string
      let tutorIdParam = null;
      if (selectedTutor) {
        if (typeof selectedTutor.userId === 'string' && selectedTutor.userId.length === 24) {
          tutorIdParam = selectedTutor.userId;
        } else if (selectedTutor.userId && typeof selectedTutor.userId === 'object' && selectedTutor.userId._id) {
          tutorIdParam = String(selectedTutor.userId._id);
        } else if (selectedTutor._id) {
          tutorIdParam = String(selectedTutor._id);
        }
      }

      // 2. Resolve clean Batch ID string
      let batchIdParam = null;
      if (selectedBatch && selectedBatch._id) {
        batchIdParam = String(selectedBatch._id);
      }

      const params = {};
      if (tutorIdParam) {
        params.tutorId = tutorIdParam;
      }
      if (batchIdParam) {
        params.batchId = batchIdParam;
      }

      if (viewMode === 'date' && selectedDate) {
        params.date = selectedDate;
      } else if (viewMode === 'month' && selectedMonth) {
        params.month = selectedMonth;
      }

      if (selectedStatus) {
        params.status = selectedStatus;
      }

      // 3. Query both direct tutor records and batch roll-call sheet concurrently
      const [adminTutorsRes, batchSheetRes] = await Promise.allSettled([
        attendanceAPI.getAdminTutors(params),
        batchIdParam ? attendanceAPI.getBatchSheet(batchIdParam) : Promise.resolve({ data: [] })
      ]);

      console.log('[DEBUG TutorHub] params sent:', JSON.stringify(params));
      console.log('[DEBUG TutorHub] adminTutors result:', adminTutorsRes.status, adminTutorsRes.status === 'fulfilled' ? JSON.stringify(adminTutorsRes.value?.data)?.slice(0,300) : adminTutorsRes.reason?.message);
      console.log('[DEBUG TutorHub] batchSheet result:', batchSheetRes.status, batchSheetRes.status === 'fulfilled' ? `${(batchSheetRes.value?.data||[]).length} records` : batchSheetRes.reason?.message);

      const directRecords = (adminTutorsRes.status === 'fulfilled' && adminTutorsRes.value?.data?.records)
        ? adminTutorsRes.value.data.records
        : [];

      const rawBatchLogs = (batchSheetRes.status === 'fulfilled' && Array.isArray(batchSheetRes.value?.data))
        ? batchSheetRes.value.data
        : [];

      console.log(`[DEBUG TutorHub] directRecords=${directRecords.length} rawBatchLogs=${rawBatchLogs.length}`);

      // 4. Combine into a unified session map keyed by calendar day (YYYY-MM-DD)
      const sessionMap = new Map();

      // Priority A: Direct tutor attendance records (explicit check-in / check-out)
      directRecords.forEach(rec => {
        const rawDate = rec.date || rec.createdAt;
        if (!rawDate) return;
        const dStr = (typeof rawDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(rawDate))
          ? rawDate.slice(0, 10)
          : new Date(rawDate).toISOString().slice(0, 10);
        sessionMap.set(dStr, rec);
      });

      // Priority B: Class roll-call conducted in Student Portal by the instructor
      const dateGroups = {};
      rawBatchLogs.forEach(item => {
        const rawDate = item.date || item.createdAt;
        if (!rawDate) return;
        const dStr = getSafeIsoDate(rawDate);
        if (!dStr) return;
        if (!dateGroups[dStr]) dateGroups[dStr] = [];
        dateGroups[dStr].push(item);
      });

      Object.entries(dateGroups).forEach(([dStr, items]) => {
        // If an explicit tutor record already exists for this date, enrich timestamps if absent
        if (sessionMap.has(dStr)) {
          const existing = sessionMap.get(dStr);
          const st = (existing.status || '').toLowerCase();
          if (st !== 'holiday' && st !== 'rescheduled') {
            if (!existing.checkInTime && items.length > 0) {
              const times = items.map(i => new Date(i.createdAt || i.date).getTime()).filter(t => !isNaN(t));
              if (times.length > 0) {
                existing.checkInTime = new Date(Math.min(...times)).toISOString();
              }
            }
          }
          return;
        }

        // Check if this date has an Academy Closure / Holiday
        const closureItem = items.find(i => {
          const st = (i.status || '').toLowerCase();
          const nt = (i.note || '').toLowerCase();
          return st === 'holiday' || st === 'rescheduled' ||
                 nt.includes('closure') || nt.includes('holiday') ||
                 nt.includes('storm') || nt.includes('rain') || nt.includes('typhoon');
        });

        if (closureItem) {
          const st = (closureItem.status || '').toLowerCase();
          const nt = (closureItem.note || '').toLowerCase();
          const closureStatus = (st === 'rescheduled' || nt.includes('reschedule')) ? 'Rescheduled' : 'Holiday';

          sessionMap.set(dStr, {
            _id: `closure_${batchIdParam}_${dStr}`,
            date: dStr,
            role: 'tutor',
            status: closureStatus,
            checkInTime: null,
            checkOutTime: null,
            batchId: selectedBatch,
            userId: selectedTutor.userId || selectedTutor,
            markedBy: closureItem.markedBy || selectedTutor,
            note: closureItem.note || 'Academy closure declared',
            createdAt: closureItem.createdAt || dStr
          });
          return;
        }

        const studentMarks = items.filter(i => i.role === 'student' || !i.role);
        if (studentMarks.length === 0) return; // Skip days with no roll-call

        // Check if this session was a Scheduled Make-up Class
        const isMakeup = studentMarks.some(i => {
          const st = (i.status || '').toLowerCase();
          const nt = (i.note || '').toLowerCase();
          return st === 'make-up' || st === 'makeup' || nt.includes('make-up') || nt.includes('makeup');
        });

        // Earliest roll call entry represents the class start / check-in time
        const times = studentMarks.map(i => new Date(i.createdAt || i.date).getTime()).filter(t => !isNaN(t));
        const earliestTime = times.length > 0 ? new Date(Math.min(...times)).toISOString() : `${dStr}T09:00:00.000Z`;

        // Check if any departure check-out was recorded
        const checkOutItem = items.find(i => i.checkOutTime);
        const checkOutTime = checkOutItem ? checkOutItem.checkOutTime : null;

        const isAbsent = studentMarks.every(i => i.status === 'Absent' || i.status === 'Cancelled');

        sessionMap.set(dStr, {
          _id: `session_${batchIdParam}_${dStr}`,
          date: dStr,
          role: 'tutor',
          status: isMakeup ? 'Make-up' : (isAbsent ? 'Absent' : 'Present'),
          checkInTime: earliestTime,
          checkOutTime: checkOutTime,
          batchId: selectedBatch,
          userId: selectedTutor.userId || selectedTutor,
          markedBy: items[0]?.markedBy || selectedTutor,
          note: isMakeup ? (items[0]?.note || 'Scheduled Make-up Class conducted') : `Class roll call conducted (${studentMarks.length} students marked)`,
          createdAt: earliestTime
        });
      });

      // 5. Build full session list (including closures, holidays, make-ups, and regular classes)
      const allSessions = Array.from(sessionMap.values());
      allSessions.sort((a, b) => {
        const da = new Date(a.date || a.createdAt).getTime();
        const db = new Date(b.date || b.createdAt).getTime();
        return db - da;
      });

      setAllBatchSessions(allSessions);

      // 6. Apply active filters (viewMode & status)
      let filtered = allSessions;
      if (viewMode === 'date' && selectedDate) {
        filtered = filtered.filter(r => {
          const rDate = (r.date ? new Date(r.date) : new Date(r.createdAt)).toISOString().slice(0, 10);
          return rDate === selectedDate;
        });
      } else if (viewMode === 'month' && selectedMonth) {
        filtered = filtered.filter(r => {
          const rDate = (r.date ? new Date(r.date) : new Date(r.createdAt)).toISOString().slice(0, 7);
          return rDate === selectedMonth;
        });
      }

      if (selectedStatus) {
        filtered = filtered.filter(r => r.status === selectedStatus);
      }

      const totalCount = filtered.length;
      const presentCount = filtered.filter(r => r.status === 'Present' || r.status === 'Make-up').length;
      const holidayCount = filtered.filter(r => r.status === 'Holiday' || r.status === 'Rescheduled').length;
      const absentCount = filtered.filter(r => r.status === 'Absent').length;
      const checkedOutCount = filtered.filter(r => r.checkOutTime != null).length;

      const baseSalary = Number(selectedTutor?.salary || 0);
      const eligibleSessions = presentCount + holidayCount;
      const attendanceRate = totalCount > 0 ? (eligibleSessions / totalCount) : (baseSalary > 0 ? 1 : 0);
      const prorationRatio = Math.min(1.0, attendanceRate);
      const calculatedSalary = totalCount > 0 ? Math.round(baseSalary * prorationRatio) : baseSalary;

      setRecords(filtered);
      setStats({
        total: totalCount,
        present: presentCount,
        holiday: holidayCount,
        absent: absentCount,
        checkedOut: checkedOutCount,
        payroll: {
          baseSalary,
          attendanceRate: Math.round(attendanceRate * 100),
          calculatedSalary,
          prorationRatio
        }
      });
    } catch (err) {
      console.warn('Batch tutor attendance fallback handling:', err.message);
      setRecords([]);
      setStats({
        total: 0,
        present: 0,
        holiday: 0,
        absent: 0,
        checkedOut: 0,
        payroll: { baseSalary: 0, attendanceRate: 0, calculatedSalary: 0, prorationRatio: 0 }
      });
    } finally {
      setLoading(false);
    }
  }, [selectedTutor, selectedBatch, viewMode, selectedDate, selectedMonth, selectedStatus]);

  useEffect(() => {
    fetchBatchTutorAttendance();
  }, [fetchBatchTutorAttendance]);

  // Export CSV Handler
  const handleExportCSV = () => {
    if (!records || records.length === 0) {
      alert('No tutor records available to export.');
      return;
    }
    const headers = ['Date', 'Instructor', 'Email', 'Batch', 'Level', 'Status', 'Check-In Time', 'Check-Out Time', 'Duration', 'Notes'];
    const rows = [headers.join(',')];

    records.forEach(r => {
      const { name, email } = getTutorDisplay(r, tutors);
      const batchName = getSafeName(r.batchId?.name || selectedBatch?.name);
      const level = r.batchId?.level || selectedBatch?.level || '';
      const dateStr = r.date ? FMT_DATE(r.date) : (r.createdAt ? FMT_DATE(r.createdAt) : '');
      const checkIn = r.checkInTime ? FMT_TIME(r.checkInTime) : (r.createdAt ? FMT_TIME(r.createdAt) : '');
      const checkOut = r.checkOutTime ? FMT_TIME(r.checkOutTime) : '';
      const duration = formatSessionDuration(r.checkInTime || r.createdAt, r.checkOutTime);
      const status = r.status || '';
      const notes = (r.note || '').replace(/"/g, '""');

      rows.push([
        `"${dateStr}"`,
        `"${name.replace(/"/g, '""')}"`,
        `"${email.replace(/"/g, '""')}"`,
        `"${batchName.replace(/"/g, '""')}"`,
        `"${level}"`,
        `"${status}"`,
        `"${checkIn}"`,
        `"${checkOut}"`,
        `"${duration}"`,
        `"${notes}"`,
      ].join(','));
    });

    const tutorName = getSafeName(selectedTutor?.name).replace(/\s+/g, '_');
    const batchName = getSafeName(selectedBatch?.name).replace(/\s+/g, '_');
    const filename = `Tutor_Attendance_${tutorName}_${batchName}_${viewMode === 'date' ? selectedDate : (viewMode === 'month' ? selectedMonth : 'All')}.csv`;
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Print Report Handler
  const handlePrint = () => {
    if (!records || records.length === 0) {
      alert('No tutor records available to print.');
      return;
    }
    const printWindow = window.open('', '_blank');
    const tutorName = getSafeName(selectedTutor?.name);
    const batchName = getSafeName(selectedBatch?.name);
    const periodLabel = viewMode === 'all' 
      ? 'All Recorded Sessions' 
      : (viewMode === 'date' ? FMT_DATE(selectedDate) : monthLabel(selectedMonth));

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Tutor Attendance - ${tutorName} (${batchName})</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding: 30px; color: #1a1a2e; }
            h1 { color: #7b1d3a; margin-bottom: 4px; font-size: 22px; }
            p { color: #666; margin-top: 0; margin-bottom: 18px; font-size: 13px; }
            .info-badge { display: inline-block; background: #f4ede4; padding: 4px 10px; border-radius: 6px; font-weight: 600; font-size: 13px; color: #7b1d3a; margin-right: 8px; }
            .stats-grid { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
            .stat-box { border: 1px solid #e2d8ce; background: #faf8f5; padding: 10px 16px; border-radius: 8px; min-width: 100px; }
            .stat-val { font-size: 20px; font-weight: 800; color: #7b1d3a; margin-bottom: 2px; }
            .stat-lbl { font-size: 10px; text-transform: uppercase; color: #7a7068; font-weight: 700; letter-spacing: 0.5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th, td { border: 1px solid #e2d8ce; padding: 8px 10px; text-align: left; font-size: 12px; }
            th { background-color: #f7f3ee; color: #5a5048; font-weight: 700; }
            .badge { display: inline-block; padding: 3px 6px; border-radius: 4px; font-weight: 700; font-size: 10px; }
            .badge-present { background: #d1fae5; color: #065f46; }
            .badge-absent { background: #fee2e2; color: #991b1b; }
            .badge-holiday { background: #fef3c7; color: #b45309; }
            .badge-makeup { background: #ede9fe; color: #6d28d9; }
          </style>
        </head>
        <body>
          <h1>🎓 Tutor Attendance Ledger</h1>
          <p>
            <span class="info-badge">Instructor: ${tutorName}</span>
            <span class="info-badge">Batch: ${batchName}</span>
            <span class="info-badge">Period: ${periodLabel}</span>
          </p>
          <div class="stats-grid">
            <div class="stat-box"><div class="stat-val">${records.length}</div><div class="stat-lbl">Total Sessions</div></div>
            <div class="stat-box"><div class="stat-val" style="color: #059669;">${records.filter(r => r.status === 'Present' || r.status === 'Make-up').length}</div><div class="stat-lbl">Present / Make-up</div></div>
            <div class="stat-box"><div class="stat-val" style="color: #d97706;">${records.filter(r => r.status === 'Holiday' || r.status === 'Rescheduled').length}</div><div class="stat-lbl">Holiday / Closure</div></div>
            <div class="stat-box"><div class="stat-val" style="color: #e11d48;">${records.filter(r => r.status === 'Absent').length}</div><div class="stat-lbl">Absent</div></div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Status</th>
                <th>Arrival (Check-in)</th>
                <th>Departure (Check-out)</th>
                <th>Duration</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${records.map(r => {
                const dStr = r.date ? FMT_DATE(r.date) : (r.createdAt ? FMT_DATE(r.createdAt) : '—');
                const st = (r.status || '').toLowerCase();
                const isCl = st === 'holiday' || st === 'rescheduled';
                const isMu = st === 'make-up' || st === 'makeup';
                const inStr = isCl ? '—' : (r.checkInTime ? FMT_TIME(r.checkInTime) : (r.createdAt ? FMT_TIME(r.createdAt) : '—'));
                const outStr = isCl ? '—' : (r.checkOutTime ? FMT_TIME(r.checkOutTime) : (r.status === 'Present' || isMu ? 'In Session' : '—'));
                const durStr = isCl ? '—' : formatSessionDuration(r.checkInTime || r.createdAt, r.checkOutTime);
                const badgeClass = isCl ? 'badge-holiday' : (isMu ? 'badge-makeup' : (r.status === 'Present' ? 'badge-present' : 'badge-absent'));
                const statusLabel = isCl ? (st === 'rescheduled' ? 'Rescheduled' : 'Holiday / Closure') : (isMu ? 'Make-up (Present)' : (r.status === 'Present' ? 'Present' : 'Absent'));
                return `
                  <tr>
                    <td><strong>${dStr}</strong></td>
                    <td><span class="badge ${badgeClass}">${statusLabel}</span></td>
                    <td>${inStr}</td>
                    <td>${outStr}</td>
                    <td><strong>${durStr}</strong></td>
                    <td>${r.note || '—'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          <script>
            window.onload = function() { window.print(); };
          </script>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Filter tutors based on search bar
  const filteredTutors = (tutors || []).filter(tutor => {
    if (!tutorSearch.trim()) return true;
    const query = tutorSearch.toLowerCase().trim();
    const name = getSafeName(tutor.name).toLowerCase();
    const email = (tutor.email || '').toLowerCase();
    const mobile = (tutor.mobile || '').toLowerCase();
    const designation = getSafeName(tutor.designation).toLowerCase();
    return name.includes(query) || email.includes(query) || mobile.includes(query) || designation.includes(query);
  });

  // Get active batches for currently selected tutor
  const currentTutorBatches = selectedTutor ? getBatchesForTutor(selectedTutor, batches) : [];

  /* ════════════════════════════════════════
     STEP 1: All Tutors (Row by Row)
  ════════════════════════════════════════ */
  if (!selectedTutor) {
    return (
      <div>
        {/* Step 1 Header & Search */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '20px', color: '#1a1a2e', fontWeight: 700 }}>
              🎓 {t('attendanceUI.tutorAttendanceTitle', 'Tutor Attendance')}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#7a7068' }}>
              Select an instructor from the list below to view their assigned batches and class attendance.
            </p>
          </div>

          {/* Quick Search */}
          <div style={{ width: '100%', maxWidth: '340px' }}>
            <input
              type="text"
              placeholder="🔍 Search instructor by name or email..."
              value={tutorSearch}
              onChange={e => setTutorSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 16px',
                borderRadius: '12px',
                border: '1px solid #e2d8ce',
                background: '#fff',
                fontSize: '13px',
                outline: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
              }}
            />
          </div>
        </div>

        {/* Tutors Row-by-Row Table */}
        <div className="dash-card">
          <div className="dash-card-header">
            <div className="dash-card-title">All Instructors ({filteredTutors.length})</div>
            <span className="badge badge-gold">Select an instructor to view batches</span>
          </div>

          {filteredTutors.length === 0 ? (
            <div className="dash-empty">
              <div className="dash-empty-icon">🎓</div>
              <div className="dash-empty-title">No instructors found</div>
              <p className="dash-empty-sub">Try searching with a different name or keyword.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Instructor</th>
                    <th>Contact Info</th>
                    <th>Designation / Specialization</th>
                    <th>Assigned Batches</th>
                    <th style={{ textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTutors.map(tutor => {
                    const tutorName = getSafeName(tutor.name);
                    const assignedBatches = getBatchesForTutor(tutor, batches);
                    const designation = getSafeName(tutor.designation) || 'Instructor';
                    const initials = tutorName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '🎓';

                    return (
                      <tr 
                        key={tutor._id}
                        style={{ cursor: 'pointer', transition: 'background 0.2s ease' }}
                        onClick={() => {
                          setSelectedTutor(tutor);
                          setSelectedBatch(null);
                        }}
                      >
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                              width: '38px', height: '38px', borderRadius: '50%',
                              background: 'linear-gradient(135deg, #7b1d3a 0%, #4a1123 100%)',
                              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '13px', fontWeight: 800, flexShrink: 0
                            }}>
                              {initials}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: '14px' }}>
                                {tutorName}
                              </div>
                              <div style={{ fontSize: '11px', color: '#888' }}>
                                ID: {String(tutor._id).slice(-6)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize: '13px', color: '#3d3730', fontWeight: 500 }}>
                            {tutor.email || '—'}
                          </div>
                          {tutor.mobile && (
                            <div style={{ fontSize: '12px', color: '#7a7068', marginTop: '2px' }}>
                              📞 {tutor.mobile}
                            </div>
                          )}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, color: '#7b1d3a', fontSize: '13px' }}>
                            {designation}
                          </div>
                          {Array.isArray(tutor.specialization) && tutor.specialization.length > 0 && (
                            <div style={{ fontSize: '12px', color: '#7a7068', marginTop: '2px' }}>
                              {tutor.specialization.join(', ')}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className="badge badge-gold" style={{ fontSize: '12px' }}>
                            📚 {assignedBatches.length} {assignedBatches.length === 1 ? 'Batch' : 'Batches'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            className="btn btn-primary btn-sm"
                            style={{
                              background: 'var(--wine)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              fontWeight: 700,
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTutor(tutor);
                              setSelectedBatch(null);
                            }}
                          >
                            View Batches ➔
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════
     STEP 2: Tutor's Batches (Row by Row)
  ════════════════════════════════════════ */
  if (selectedTutor && !selectedBatch) {
    const tutorName = getSafeName(selectedTutor.name);

    return (
      <div>
        {/* Breadcrumb Navigation */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setSelectedTutor(null)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: '#fff',
              border: '1px solid #e2d8ce',
              padding: '8px 16px',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '13px',
              color: 'var(--wine)',
              boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
            }}
          >
            ← All Instructors
          </button>
          <span style={{ color: '#bbb', fontSize: '16px' }}>/</span>
          <span style={{ fontWeight: 800, color: '#1a1a2e', fontSize: '15px' }}>
            🎓 {tutorName}
          </span>
        </div>

        {/* Selected Tutor Identity Card */}
        <div style={{
          background: 'linear-gradient(135deg, #fcfaf7 0%, #f4ede4 100%)',
          borderRadius: '16px',
          padding: '20px 24px',
          marginBottom: '24px',
          border: '1px solid #e2d8ce',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: '#7b1d3a', letterSpacing: '0.5px' }}>
              Selected Instructor
            </div>
            <h2 style={{ margin: '4px 0 6px', fontSize: '22px', fontWeight: 800, color: '#1a1a2e' }}>
              {tutorName}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: '#5a5048' }}>
              {selectedTutor.email && <span>✉️ {selectedTutor.email}</span>}
              {selectedTutor.mobile && <span>📞 {selectedTutor.mobile}</span>}
              <span>📚 {currentTutorBatches.length} Assigned {currentTutorBatches.length === 1 ? 'Batch' : 'Batches'}</span>
            </div>
          </div>


        </div>

        {/* Batches Table (Row by Row) */}
        <div className="dash-card">
          <div className="dash-card-header">
            <div className="dash-card-title">
              Batches Taught by {tutorName} ({currentTutorBatches.length})
            </div>
            <span className="badge badge-gold">Select a batch to view attendance</span>
          </div>

          {currentTutorBatches.length === 0 ? (
            <div className="dash-empty" style={{ padding: '40px 20px' }}>
              <div className="dash-empty-icon">📚</div>
              <div className="dash-empty-title">No Batches Assigned</div>
              <p className="dash-empty-sub" style={{ maxWidth: '400px', margin: '8px auto 20px' }}>
                There are no active batches directly linked to {tutorName}. You can assign batches in the Faculty Room or pick any academy batch below:
              </p>
              {batches.length > 0 && (
                <div style={{ maxWidth: '400px', margin: '0 auto', textAlign: 'left' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#7a7068', marginBottom: '6px' }}>
                    Select Any Academy Batch to View:
                  </label>
                  <select
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2d8ce', fontSize: '13px', fontWeight: 600 }}
                    onChange={e => {
                      const b = batches.find(x => x._id === e.target.value);
                      if (b) setSelectedBatch(b);
                    }}
                    defaultValue=""
                  >
                    <option value="" disabled>-- Choose a batch --</option>
                    {batches.map(b => (
                      <option key={b._id} value={b._id}>
                        {getSafeName(b.name)} ({b.level})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          ) : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>Batch Name</th>
                    <th>Level / Category</th>
                    <th>Class Schedule</th>
                    <th>Enrolled Students</th>
                    <th style={{ textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentTutorBatches.map(batch => {
                    const batchName = getSafeName(batch.name);
                    const scheduleStr = (batch.days && batch.days.length > 0)
                      ? batch.days.join(', ')
                      : 'Flexible';
                    const timeStr = (batch.startTime && batch.endTime)
                      ? `${batch.startTime} - ${batch.endTime}`
                      : '—';
                    const studentsCount = (batch.students || []).length;

                    return (
                      <tr
                        key={batch._id}
                        style={{ cursor: 'pointer', transition: 'background 0.2s ease' }}
                        onClick={() => setSelectedBatch(batch)}
                      >
                        <td>
                          <div style={{ fontWeight: 700, color: 'var(--wine)', fontSize: '14px' }}>
                            {batchName}
                          </div>
                          <div style={{ fontSize: '11px', color: '#888' }}>
                            ID: {String(batch._id).slice(-6)}
                          </div>
                        </td>
                        <td>
                          <span className="badge badge-blue">
                            {batch.level || 'Standard'}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: '13px', color: '#1a1a2e' }}>
                            🗓️ {scheduleStr}
                          </div>
                          <div style={{ fontSize: '12px', color: '#7a7068', marginTop: '2px' }}>
                            ⏰ {timeStr}
                          </div>
                        </td>
                        <td>
                          <span className="badge badge-gold">
                            👥 {studentsCount} {studentsCount === 1 ? 'Student' : 'Students'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            className="btn btn-primary btn-sm"
                            style={{
                              background: 'var(--wine)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              fontWeight: 700,
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBatch(batch);
                            }}
                          >
                            View Attendance ➔
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════
     STEP 3: That Batch's Tutor Attendance Ledger
  ════════════════════════════════════════ */
  const tutorName = getSafeName(selectedTutor.name);
  const batchName = getSafeName(selectedBatch.name);

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => {
            setSelectedTutor(null);
            setSelectedBatch(null);
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: '#fff',
            border: '1px solid #e2d8ce',
            padding: '7px 14px',
            borderRadius: '10px',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: '12px',
            color: 'var(--wine)'
          }}
        >
          👥 All Instructors
        </button>
        <span style={{ color: '#bbb' }}>/</span>
        <button
          onClick={() => setSelectedBatch(null)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: '#fff',
            border: '1px solid #e2d8ce',
            padding: '7px 14px',
            borderRadius: '10px',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: '12px',
            color: 'var(--wine)'
          }}
        >
          🎓 {tutorName} (Batches)
        </button>
        <span style={{ color: '#bbb' }}>/</span>
        <span style={{ fontWeight: 800, color: '#1a1a2e', fontSize: '14px' }}>
          📚 {batchName}
        </span>
      </div>

      {/* Selected Batch & Tutor Header Card */}
      <div style={{
        background: 'linear-gradient(135deg, #7b1d3a 0%, #4a1123 100%)',
        borderRadius: '18px',
        padding: '20px 24px',
        marginBottom: '24px',
        color: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px',
        boxShadow: '0 8px 24px rgba(123, 29, 58, 0.25)'
      }}>
        <div>
          <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', opacity: 0.85, textTransform: 'uppercase' }}>
            Batch Attendance Ledger
          </div>
          <h2 style={{ margin: '4px 0 6px', fontSize: '22px', fontWeight: 800 }}>
            {batchName}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', fontSize: '13px', opacity: 0.95 }}>
            <span>🎓 Instructor: <strong>{tutorName}</strong></span>
            {selectedBatch.level && <span>🏷️ Level: <strong>{selectedBatch.level}</strong></span>}
            {selectedBatch.days && selectedBatch.days.length > 0 && (
              <span>🗓️ Schedule: <strong>{selectedBatch.days.join(', ')}</strong></span>
            )}
            {selectedBatch.startDate && selectedBatch.endDate && (
              <span>📅 Batch Duration: <strong>{FMT_DATE(selectedBatch.startDate)} – {FMT_DATE(selectedBatch.endDate)}</strong></span>
            )}
          </div>
        </div>


      </div>

      {/* Option B: Monthly Payroll & Salary Settlement Card */}
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        padding: '20px 24px',
        border: '1.5px solid var(--wine)',
        boxShadow: '0 8px 24px rgba(139, 29, 63, 0.08)',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            width: '46px',
            height: '46px',
            borderRadius: '12px',
            background: 'var(--wine)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '22px'
          }}>
            💵
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '16px', fontWeight: 800, color: '#1e293b' }}>
                Monthly Salary Settlement
              </span>
              <span style={{
                fontSize: '11px',
                fontWeight: 700,
                padding: '3px 8px',
                borderRadius: '12px',
                background: (stats.payroll?.attendanceRate || 0) >= 90 ? '#ecfdf5' : '#fffbeb',
                color: (stats.payroll?.attendanceRate || 0) >= 90 ? '#059669' : '#d97706',
                border: `1px solid ${(stats.payroll?.attendanceRate || 0) >= 90 ? '#a7f3d0' : '#fde68a'}`
              }}>
                {stats.payroll?.attendanceRate || 0}% Attendance Rate
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
              Option B: Fixed Base Salary with Attendance-Verified Proration • {stats.present + stats.holiday} of {stats.total} Sessions Verified
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          {stats.payroll?.baseSalary > 0 && (
            <div style={{ textAlign: 'right', borderRight: '1px solid #e2e8f0', paddingRight: '20px' }}>
              <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
                Base Monthly Salary
              </div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#334155' }}>
                HK$ {Number(stats.payroll?.baseSalary || 0).toLocaleString()}
              </div>
            </div>
          )}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '11px', color: 'var(--wine)', textTransform: 'uppercase', fontWeight: 700 }}>
              Calculated Monthly Payout
            </div>
            <div style={{ fontSize: '26px', fontWeight: 900, color: 'var(--wine)' }}>
              HK$ {Number(stats.payroll?.calculatedSalary || 0).toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* 5 Attendance Summary Cards for this Batch */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{
          background: '#fff', borderRadius: '14px', padding: '18px 20px',
          border: '1px solid #e2d8ce', boxShadow: '0 4px 15px rgba(0,0,0,0.02)'
        }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#7a7068', textTransform: 'uppercase', marginBottom: '4px' }}>
            Total Sessions Logged
          </div>
          <div style={{ fontSize: '32px', fontWeight: 800, color: 'var(--wine)' }}>
            {stats.total}
          </div>
        </div>

        <div style={{
          background: '#fff', borderRadius: '14px', padding: '18px 20px',
          border: '1px solid #e2d8ce', boxShadow: '0 4px 15px rgba(0,0,0,0.02)'
        }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#059669', textTransform: 'uppercase', marginBottom: '4px' }}>
            Present / Make-up
          </div>
          <div style={{ fontSize: '32px', fontWeight: 800, color: '#059669' }}>
            {stats.present}
          </div>
        </div>

        <div style={{
          background: '#fff', borderRadius: '14px', padding: '18px 20px',
          border: '1px solid #e2d8ce', boxShadow: '0 4px 15px rgba(0,0,0,0.02)'
        }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#d97706', textTransform: 'uppercase', marginBottom: '4px' }}>
            Holiday / Closure
          </div>
          <div style={{ fontSize: '32px', fontWeight: 800, color: '#d97706' }}>
            {stats.holiday}
          </div>
        </div>

        <div style={{
          background: '#fff', borderRadius: '14px', padding: '18px 20px',
          border: '1px solid #e2d8ce', boxShadow: '0 4px 15px rgba(0,0,0,0.02)'
        }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#e11d48', textTransform: 'uppercase', marginBottom: '4px' }}>
            Absent / Cancelled
          </div>
          <div style={{ fontSize: '32px', fontWeight: 800, color: '#e11d48' }}>
            {stats.absent}
          </div>
        </div>

        <div style={{
          background: '#fff', borderRadius: '14px', padding: '18px 20px',
          border: '1px solid #e2d8ce', boxShadow: '0 4px 15px rgba(0,0,0,0.02)'
        }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', marginBottom: '4px' }}>
            Check-outs Completed
          </div>
          <div style={{ fontSize: '32px', fontWeight: 800, color: '#6366f1' }}>
            {stats.checkedOut}
          </div>
        </div>
      </div>

      {/* Filter Bar & Export Actions */}
      <div style={{
        background: '#fff', borderRadius: '16px', padding: '16px 20px', marginBottom: '24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f0e6da',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap'
      }}>
        {/* View Mode (All / Daily / Monthly) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', background: '#f0e6da', padding: '3px', borderRadius: '12px' }}>
            <button
              onClick={() => setViewMode('all')}
              style={{
                padding: '6px 14px', borderRadius: '10px', border: 'none',
                background: viewMode === 'all' ? '#fff' : 'transparent',
                color: viewMode === 'all' ? '#7b1d3a' : '#7a7068',
                fontWeight: viewMode === 'all' ? 700 : 500, fontSize: '13px', cursor: 'pointer',
                boxShadow: viewMode === 'all' ? '0 2px 6px rgba(0,0,0,0.08)' : 'none'
              }}
            >
              All Dates
            </button>
            <button
              onClick={() => setViewMode('date')}
              style={{
                padding: '6px 14px', borderRadius: '10px', border: 'none',
                background: viewMode === 'date' ? '#fff' : 'transparent',
                color: viewMode === 'date' ? '#7b1d3a' : '#7a7068',
                fontWeight: viewMode === 'date' ? 700 : 500, fontSize: '13px', cursor: 'pointer',
                boxShadow: viewMode === 'date' ? '0 2px 6px rgba(0,0,0,0.08)' : 'none'
              }}
            >
              📅 Specific Date
            </button>
            <button
              onClick={() => setViewMode('month')}
              style={{
                padding: '6px 14px', borderRadius: '10px', border: 'none',
                background: viewMode === 'month' ? '#fff' : 'transparent',
                color: viewMode === 'month' ? '#7b1d3a' : '#7a7068',
                fontWeight: viewMode === 'month' ? 700 : 500, fontSize: '13px', cursor: 'pointer',
                boxShadow: viewMode === 'month' ? '0 2px 6px rgba(0,0,0,0.08)' : 'none'
              }}
            >
              🗓️ Monthly Ledger
            </button>
          </div>

          {/* Conditional Date / Month Input */}
          {viewMode === 'date' && (
            <input
              type="date"
              value={selectedDate}
              min={selectedBatch?.startDate ? selectedBatch.startDate.slice(0, 10) : undefined}
              max={selectedBatch?.endDate ? selectedBatch.endDate.slice(0, 10) : undefined}
              onChange={e => setSelectedDate(e.target.value)}
              style={{
                padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2d8ce',
                fontSize: '13px', fontWeight: 600, color: '#3d3730', outline: 'none'
              }}
            />
          )}

          {viewMode === 'month' && (
            <select
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
              style={{
                padding: '8px 14px', borderRadius: '8px', border: '1px solid #e2d8ce',
                background: '#fff', fontSize: '13px', fontWeight: 600, color: '#3d3730', outline: 'none',
                minWidth: '160px', cursor: 'pointer'
              }}
            >
              {batchMonths.map(m => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </select>
          )}

          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={e => setSelectedStatus(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2d8ce',
              background: '#fcfaf7', fontSize: '13px', fontWeight: 600, color: '#3d3730', outline: 'none'
            }}
          >
            <option value="">All Statuses</option>
            <option value="Present">✅ Present</option>
            <option value="Make-up">🔁 Make-up Class</option>
            <option value="Holiday">🏖️ Holiday / Closure</option>
            <option value="Rescheduled">📅 Rescheduled</option>
            <option value="Absent">❌ Absent</option>
          </select>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={handleExportCSV}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '9px 16px', borderRadius: '10px',
              border: '1px solid #7b1d3a', background: '#fff',
              color: '#7b1d3a', fontWeight: 700, fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            📥 Export CSV
          </button>
          <button
            onClick={handlePrint}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '9px 16px', borderRadius: '10px',
              border: 'none', background: 'var(--wine)',
              color: '#fff', fontWeight: 700, fontSize: '13px',
              cursor: 'pointer', boxShadow: '0 4px 12px rgba(123, 29, 58, 0.2)'
            }}
          >
            🖨️ Print Report
          </button>
        </div>
      </div>

      {/* Main Attendance Records Table */}
      <div className="dash-card">
        <div className="dash-card-header">
          <div className="dash-card-title">
            Attendance Records for {tutorName}
          </div>
          <span className="badge badge-gold">
            {records.length} {records.length === 1 ? 'Record' : 'Records'}
          </span>
        </div>

        {loading ? (
          <div className="dash-loading">⏳ Analyzing batch attendance records...</div>
        ) : records.length === 0 ? (
          <div className="dash-empty" style={{ padding: '40px 20px' }}>
            <div className="dash-empty-icon">📅</div>
            <div className="dash-empty-title">No Attendance Records Found</div>
            <p className="dash-empty-sub" style={{ maxWidth: '420px', margin: '8px auto' }}>
              No attendance records match your filter for <strong>{tutorName}</strong> in <strong>{batchName}</strong>. Once the instructor self-marks attendance or clocks in, timestamps and durations will appear here.
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Class Date</th>
                  <th>Attendance Status</th>
                  <th>Arrival (Check-in)</th>
                  <th>Departure (Check-out)</th>
                  <th>Duration</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {records.map(log => {
                  const statusLower = (log.status || '').toLowerCase();
                  const isPresent = statusLower === 'present';
                  const isMakeup = statusLower === 'make-up' || statusLower === 'makeup';
                  const isHoliday = statusLower === 'holiday';
                  const isRescheduled = statusLower === 'rescheduled';
                  const isClosure = isHoliday || isRescheduled;

                  const checkInDisplay = isClosure
                    ? '—'
                    : (log.checkInTime 
                        ? FMT_TIME(log.checkInTime) 
                        : (log.createdAt ? FMT_TIME(log.createdAt) : '—'));

                  const checkOutDisplay = isClosure
                    ? '—'
                    : (log.checkOutTime 
                        ? FMT_TIME(log.checkOutTime) 
                        : ((isPresent || isMakeup) ? <span className="badge badge-gold" style={{ fontSize: '11px' }}>⏱️ In Session</span> : '—'));

                  const durationDisplay = isClosure 
                    ? '—' 
                    : formatSessionDuration(log.checkInTime || log.createdAt, log.checkOutTime);

                  let badgeEl = null;
                  if (isPresent) {
                    badgeEl = <span className="badge badge-green">✅ Present</span>;
                  } else if (isMakeup) {
                    badgeEl = <span className="badge" style={{ background: '#ede9fe', color: '#6d28d9', border: '1px solid #ddd6fe' }}>🔁 Make-up Class</span>;
                  } else if (isHoliday) {
                    badgeEl = <span className="badge" style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' }}>🏖️ Holiday / Closure</span>;
                  } else if (isRescheduled) {
                    badgeEl = <span className="badge" style={{ background: '#e0e7ff', color: '#3730a3', border: '1px solid #c7d2fe' }}>📅 Rescheduled</span>;
                  } else {
                    badgeEl = <span className="badge badge-red">❌ Absent</span>;
                  }

                  return (
                    <tr key={log._id}>
                      <td style={{ fontWeight: 600, color: '#1a1a2e' }}>
                        {log.date ? FMT_DATE(log.date) : (log.createdAt ? FMT_DATE(log.createdAt) : '—')}
                      </td>
                      <td>
                        {badgeEl}
                      </td>
                      <td style={{ fontSize: '13px', fontWeight: 600, color: isClosure ? '#aaa' : '#059669' }}>
                        {checkInDisplay}
                      </td>
                      <td style={{ fontSize: '13px' }}>
                        {checkOutDisplay}
                      </td>
                      <td>
                        <span style={{ fontWeight: 700, color: durationDisplay !== '—' ? '#7b1d3a' : '#aaa' }}>
                          {durationDisplay}
                        </span>
                      </td>
                      <td className="text-sm" style={{ color: log.note ? '#3d3730' : '#aaa' }}>
                        {log.note || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════
   Student Attendance Hub (Hierarchical Flow)
   Step 1: All Batches in rows
   Step 2: Selected Batch Student Attendance
════════════════════════════════════════ */
function getBatchInstructorName(batch, tutors = []) {
  if (!batch) return 'Unassigned';
  const tutorId = String(typeof batch.tutor === 'object' && batch.tutor !== null ? (batch.tutor._id || '') : (batch.tutor || ''));
  const matched = (tutors || []).find(t => 
    String(t._id) === tutorId || 
    String(t.userId?._id || t.userId) === tutorId
  );
  if (matched) {
    return tField(matched.name);
  }
  if (typeof batch.tutor === 'object' && batch.tutor && batch.tutor.name) {
    return tField(batch.tutor.name);
  }
  return 'Unassigned';
}

function getStudentDisplay(log, students = []) {
  const studentDoc = (typeof log.studentId === 'object' && log.studentId) ? log.studentId : null;
  const studentIdRef = log.studentId?._id || log.studentId;
  const userIdRef = log.userId?._id || log.userId;
  
  const matched = (students || []).find(s => 
    (studentIdRef && String(s._id) === String(studentIdRef)) || 
    (userIdRef && String(s.userId?._id || s.userId) === String(userIdRef))
  );

  const name = tField(studentDoc?.name) || tField(log.userId?.name) || (matched && tField(matched.name)) || 'Student';
  const rollNo = studentDoc?.studentId || matched?.studentId || studentDoc?.rollNumber || matched?.rollNumber || log.userId?.studentId || '';
  return { name, rollNo };
}

function getSafeIsoDate(raw) {
  if (!raw) return '';
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }
  try {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  } catch (e) {
    return '';
  }
}

function getSafeIsoMonth(raw) {
  if (!raw) return '';
  if (typeof raw === 'string' && /^\d{4}-\d{2}/.test(raw)) {
    return raw.slice(0, 7);
  }
  try {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 7);
  } catch (e) {
    return '';
  }
}

function StudentAttendanceHub({ batches = [], tutors = [], students = [], dailyStats = {} }) {
  // Navigation / Selection State
  const [selectedBatch, setSelectedBatch] = useState(null);

  // Search & Filter States
  const [batchSearch, setBatchSearch]     = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [viewMode, setViewMode]           = useState('all'); // 'all' | 'date' | 'month'
  const [selectedDate, setSelectedDate]   = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedStatus, setSelectedStatus] = useState('');
  const [activeSubView, setActiveSubView] = useState('logs'); // 'logs' | 'matrix'

  // Batch Attendance Records State
  const [allBatchLogs, setAllBatchLogs]   = useState([]);
  const [loading, setLoading]             = useState(false);

  // Fetch Batch Attendance Sheet when a Batch is selected
  const fetchBatchStudentAttendance = useCallback(async () => {
    if (!selectedBatch) {
      setAllBatchLogs([]);
      return;
    }
    setLoading(true);
    try {
      const batchId = selectedBatch._id || selectedBatch;
      console.log(`[DEBUG StudentHub] fetching attendance for batchId="${batchId}"`);
      const res = await attendanceAPI.getBatchSheet(batchId);
      console.log(`[DEBUG StudentHub] API response status=${res.status} isArray=${Array.isArray(res.data)} length=${Array.isArray(res.data) ? res.data.length : 'N/A'}`);
      if (Array.isArray(res.data) && res.data.length > 0) {
        console.log('[DEBUG StudentHub] First record sample:', JSON.stringify(res.data[0]));
      }

      const rawRecords = (Array.isArray(res.data) ? res.data : []).filter(
        i => i.role === 'student' || !i.role
      );
      console.log(`[DEBUG StudentHub] after role filter (student/none): ${rawRecords.length} records`);

      // Deduplicate by Student + Calendar Date (a student can only have 1 attendance record per class day)
      const studentSessionMap = new Map();

      rawRecords.forEach(item => {
        const studentDoc = (typeof item.studentId === 'object' && item.studentId) ? item.studentId : null;
        const studentIdRef = studentDoc?._id || item.studentId;
        const userIdRef = item.userId?._id || item.userId;
        const studentKey = String(studentIdRef || userIdRef || '');
        if (!studentKey) return;

        const rawDate = item.date || item.createdAt;
        if (!rawDate) return;
        const dStr = getSafeIsoDate(rawDate);
        if (!dStr) return;

        const key = `${studentKey}_${dStr}`;

        if (!studentSessionMap.has(key)) {
          studentSessionMap.set(key, item);
        } else {
          const existing = studentSessionMap.get(key);
          const itemStatus = (item.status || '').toLowerCase();
          const existingStatus = (existing.status || '').toLowerCase();

          // Rule: 'Present' always takes precedence over 'Make-up' or 'Absent'
          if (itemStatus === 'present' && existingStatus !== 'present') {
            studentSessionMap.set(key, item);
          } else if (existingStatus === 'present' && itemStatus !== 'present') {
            // Keep existing Present
          } else {
            // Same status: keep the latest recorded entry
            const tItem = new Date(item.createdAt || item.date).getTime();
            const tExist = new Date(existing.createdAt || existing.date).getTime();
            if (tItem >= tExist) {
              studentSessionMap.set(key, item);
            }
          }
        }
      });

      setAllBatchLogs(Array.from(studentSessionMap.values()));
    } catch (err) {
      console.warn('Failed to load batch student records:', err.message);
      setAllBatchLogs([]);
    } finally {
      setLoading(false);
    }
  }, [selectedBatch]);

  useEffect(() => {
    fetchBatchStudentAttendance();
  }, [fetchBatchStudentAttendance]);

  // Compute only the valid executing months for the selected batch + recorded sessions
  const batchMonths = useMemo(() => {
    return getBatchExecutingMonths(selectedBatch, allBatchLogs);
  }, [selectedBatch, allBatchLogs]);

  // Synchronize selectedMonth and selectedDate when selectedBatch changes
  useEffect(() => {
    if (selectedBatch) {
      const bMonths = getBatchExecutingMonths(selectedBatch, allBatchLogs);
      if (bMonths.length > 0) {
        setSelectedMonth(prev => {
          // Preserve user-selected month if it is already within this batch's executing months
          if (prev && bMonths.includes(prev)) return prev;
          const todayMonth = new Date().toISOString().slice(0, 7);
          return bMonths.includes(todayMonth) ? todayMonth : bMonths[0];
        });
      }

      const todayStr = new Date().toISOString().split('T')[0];
      const startKey = selectedBatch.startDate ? selectedBatch.startDate.slice(0, 10) : null;
      const endKey = selectedBatch.endDate ? selectedBatch.endDate.slice(0, 10) : null;
      if (startKey && todayStr < startKey) {
        setSelectedDate(startKey);
      } else if (endKey && todayStr > endKey) {
        setSelectedDate(endKey);
      }
    }
  }, [selectedBatch?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Extract all available levels across academy batches
  const availableLevels = useMemo(() => {
    const set = new Set();
    (batches || []).forEach(b => {
      if (b.level) set.add(b.level);
    });
    return Array.from(set);
  }, [batches]);

  // Count total enrolled students across all academy batches
  const totalEnrolledStudents = useMemo(() => {
    const ids = new Set();
    (batches || []).forEach(b => {
      (b.students || []).forEach(s => {
        const sid = typeof s === 'object' ? s._id : s;
        if (sid) ids.add(String(sid));
      });
    });
    return ids.size;
  }, [batches]);

  // Filter batches for Step 1
  const filteredBatches = useMemo(() => {
    return (batches || []).filter(b => {
      const bName = getSafeName(b.name).toLowerCase();
      const bLevel = (b.level || '').toLowerCase();
      const instructorName = getBatchInstructorName(b, tutors).toLowerCase();
      const bDays = (b.days || []).join(' ').toLowerCase();

      if (selectedLevel && (b.level || '').toLowerCase() !== selectedLevel.toLowerCase()) {
        return false;
      }

      if (batchSearch) {
        const q = batchSearch.trim().toLowerCase();
        const match = bName.includes(q) || bLevel.includes(q) || instructorName.includes(q) || bDays.includes(q) || String(b._id).slice(-6).includes(q);
        if (!match) return false;
      }

      return true;
    });
  }, [batches, tutors, selectedLevel, batchSearch]);

  // Filter batch records for Step 2
  const filteredLogs = useMemo(() => {
    let list = allBatchLogs;

    if (viewMode === 'date' && selectedDate) {
      list = list.filter(r => {
        const raw = r.date || r.createdAt;
        if (!raw) return false;
        return getSafeIsoDate(raw) === selectedDate;
      });
    } else if (viewMode === 'month' && selectedMonth) {
      list = list.filter(r => {
        const raw = r.date || r.createdAt;
        if (!raw) return false;
        return getSafeIsoMonth(raw) === selectedMonth;
      });
    }

    if (selectedStatus) {
      list = list.filter(r => {
        const s = (r.status || '').toLowerCase();
        return s === selectedStatus.toLowerCase();
      });
    }

    return [...list].sort((a, b) => {
      const da = new Date(a.date || a.createdAt).getTime();
      const db = new Date(b.date || b.createdAt).getTime();
      return db - da;
    });
  }, [allBatchLogs, viewMode, selectedDate, selectedMonth, selectedStatus]);

  // Summary stats for the selected batch
  const batchStats = useMemo(() => {
    const total = filteredLogs.length;
    const present = filteredLogs.filter(r => (r.status || '').toLowerCase() === 'present').length;
    const absent = filteredLogs.filter(r => (r.status || '').toLowerCase() === 'absent').length;
    const other = total - present - absent;
    return { total, present, absent, other };
  }, [filteredLogs]);

  const batchName = getSafeName(selectedBatch?.name);

  // Export CSV Handler for Student Attendance
  const handleExportCSV = () => {
    if (!filteredLogs || filteredLogs.length === 0) {
      alert('No student attendance records available to export.');
      return;
    }
    const headers = ['Date', 'Student ID', 'Student Name', 'Batch', 'Level', 'Status', 'Time Marked', 'Marked By', 'Notes'];
    const rows = [headers.join(',')];

    filteredLogs.forEach(r => {
      const { name, rollNo } = getStudentDisplay(r, students);
      const level = selectedBatch?.level || '';
      const dateStr = r.date ? FMT_DATE(r.date) : (r.createdAt ? FMT_DATE(r.createdAt) : '');
      const timeStr = r.createdAt ? FMT_TIME(r.createdAt) : '';
      const status = r.status || '';
      const markedBy = (typeof r.markedBy === 'object' && r.markedBy?.name) ? tField(r.markedBy.name) : 'Instructor';
      const notes = (r.note || '').replace(/"/g, '""');

      rows.push([
        `"${dateStr}"`,
        `"${rollNo}"`,
        `"${name.replace(/"/g, '""')}"`,
        `"${batchName.replace(/"/g, '""')}"`,
        `"${level}"`,
        `"${status}"`,
        `"${timeStr}"`,
        `"${markedBy.replace(/"/g, '""')}"`,
        `"${notes}"`,
      ].join(','));
    });

    const batchSlug = batchName.replace(/\s+/g, '_');
    const periodLabel = viewMode === 'date' ? selectedDate : (viewMode === 'month' ? selectedMonth : 'All');
    const filename = `Student_Attendance_${batchSlug}_${periodLabel}.csv`;
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Print Report Handler
  const handlePrint = () => {
    if (!filteredLogs || filteredLogs.length === 0) {
      alert('No student records available to print.');
      return;
    }
    const printWindow = window.open('', '_blank');
    const periodLabel = viewMode === 'all' 
      ? 'All Recorded Sessions' 
      : (viewMode === 'date' ? FMT_DATE(selectedDate) : monthLabel(selectedMonth));

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Student Attendance - ${batchName}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding: 30px; color: #1a1a2e; }
            h1 { color: #7b1d3a; margin-bottom: 4px; font-size: 22px; }
            p { color: #666; margin-top: 0; margin-bottom: 18px; font-size: 13px; }
            .info-badge { display: inline-block; background: #f4ede4; padding: 4px 10px; border-radius: 6px; font-weight: 600; font-size: 13px; color: #7b1d3a; margin-right: 8px; }
            .stats-grid { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
            .stat-box { border: 1px solid #e2d8ce; background: #faf8f5; padding: 10px 16px; border-radius: 8px; min-width: 100px; }
            .stat-val { font-size: 20px; font-weight: 800; color: #7b1d3a; margin-bottom: 2px; }
            .stat-lbl { font-size: 10px; text-transform: uppercase; color: #7a7068; font-weight: 700; letter-spacing: 0.5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th, td { border: 1px solid #e2d8ce; padding: 8px 10px; text-align: left; font-size: 12px; }
            th { background-color: #f7f3ee; color: #5a5048; font-weight: 700; }
            .badge { display: inline-block; padding: 3px 6px; border-radius: 4px; font-weight: 700; font-size: 10px; }
            .badge-present { background: #d1fae5; color: #065f46; }
            .badge-absent { background: #fee2e2; color: #991b1b; }
          </style>
        </head>
        <body>
          <h1>Student Attendance Sheet</h1>
          <p>
            <span class="info-badge">Batch: ${batchName}</span>
            <span class="info-badge">Period: ${periodLabel}</span>
          </p>
          <div class="stats-grid">
            <div class="stat-box"><div class="stat-val">${batchStats.total}</div><div class="stat-lbl">Total Records</div></div>
            <div class="stat-box"><div class="stat-val">${batchStats.present}</div><div class="stat-lbl">Present</div></div>
            <div class="stat-box"><div class="stat-val">${batchStats.absent}</div><div class="stat-lbl">Absent</div></div>
            <div class="stat-box"><div class="stat-val">${batchStats.other}</div><div class="stat-lbl">Other / Leave</div></div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Student</th>
                <th>Student ID</th>
                <th>Status</th>
                <th>Class Date</th>
                <th>Time Marked</th>
                <th>Marked By</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${filteredLogs.map(r => {
                const { name, rollNo } = getStudentDisplay(r, students);
                const dateStr = r.date ? FMT_DATE(r.date) : (r.createdAt ? FMT_DATE(r.createdAt) : '—');
                const timeStr = r.createdAt ? FMT_TIME(r.createdAt) : '—';
                const isPresent = (r.status || '').toLowerCase() === 'present';
                const markedBy = (typeof r.markedBy === 'object' && r.markedBy?.name) ? tField(r.markedBy.name) : 'Instructor';

                return `
                  <tr>
                    <td><strong>${name}</strong></td>
                    <td>${rollNo || '—'}</td>
                    <td><span class="badge ${isPresent ? 'badge-present' : 'badge-absent'}">${r.status || 'Present'}</span></td>
                    <td>${dateStr}</td>
                    <td>${timeStr}</td>
                    <td>${markedBy}</td>
                    <td>${r.note || '—'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <div>
      {/* ── STEP 1: All Academy Batches Row by Row ── */}
      {!selectedBatch ? (
        <div>
          {/* Academy Overview Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #7b1d3a 0%, #4a1123 100%)',
              borderRadius: '16px', padding: '20px 24px', color: '#fff',
              boxShadow: '0 8px 24px rgba(123, 29, 58, 0.25)', position: 'relative', overflow: 'hidden'
            }}>
              <div style={{ position: 'absolute', right: '-10px', top: '-10px', fontSize: '64px', opacity: 0.15 }}>📚</div>
              <div style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '0.5px', opacity: 0.9, textTransform: 'uppercase' }}>
                Total Batches
              </div>
              <div style={{ fontSize: '36px', fontWeight: 800, marginTop: '4px' }}>
                {batches.length}
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              borderRadius: '16px', padding: '20px 24px', color: '#fff',
              boxShadow: '0 8px 24px rgba(37, 99, 235, 0.25)', position: 'relative', overflow: 'hidden'
            }}>
              <div style={{ position: 'absolute', right: '-10px', top: '-10px', fontSize: '64px', opacity: 0.15 }}>👥</div>
              <div style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '0.5px', opacity: 0.9, textTransform: 'uppercase' }}>
                Enrolled Students
              </div>
              <div style={{ fontSize: '36px', fontWeight: 800, marginTop: '4px' }}>
                {totalEnrolledStudents}
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '16px', padding: '20px 24px', color: '#fff',
              boxShadow: '0 8px 24px rgba(16, 185, 129, 0.25)', position: 'relative', overflow: 'hidden'
            }}>
              <div style={{ position: 'absolute', right: '-10px', top: '-10px', fontSize: '64px', opacity: 0.15 }}>✅</div>
              <div style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '0.5px', opacity: 0.9, textTransform: 'uppercase' }}>
                Students Present Today
              </div>
              <div style={{ fontSize: '36px', fontWeight: 800, marginTop: '4px' }}>
                {dailyStats.studentsPresent || 0}
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
              borderRadius: '16px', padding: '20px 24px', color: '#fff',
              boxShadow: '0 8px 24px rgba(244, 63, 94, 0.25)', position: 'relative', overflow: 'hidden'
            }}>
              <div style={{ position: 'absolute', right: '-10px', top: '-10px', fontSize: '64px', opacity: 0.15 }}>❌</div>
              <div style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '0.5px', opacity: 0.9, textTransform: 'uppercase' }}>
                Students Absent Today
              </div>
              <div style={{ fontSize: '36px', fontWeight: 800, marginTop: '4px' }}>
                {dailyStats.studentsAbsent || 0}
              </div>
            </div>
          </div>

          {/* Search & Level Filter Toolbar */}
          <div style={{
            background: '#fff', borderRadius: '16px', padding: '16px 20px', marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f0e6da',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: '260px', maxWidth: '500px' }}>
              <input
                type="text"
                placeholder="🔍 Search batches by name, tutor, level..."
                value={batchSearch}
                onChange={e => setBatchSearch(e.target.value)}
                style={{
                  width: '100%', padding: '10px 16px', borderRadius: '10px',
                  border: '1px solid #e2d8ce', background: '#fcfaf7',
                  fontSize: '13px', fontWeight: 600, color: '#3d3730', outline: 'none'
                }}
              />
              {batchSearch && (
                <button
                  onClick={() => setBatchSearch('')}
                  style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '13px', marginLeft: '-32px' }}
                >
                  ✕
                </button>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '12px', fontWeight: 700, color: '#7a7068', textTransform: 'uppercase' }}>
                Level:
              </label>
              <select
                value={selectedLevel}
                onChange={e => setSelectedLevel(e.target.value)}
                style={{
                  padding: '9px 14px', borderRadius: '10px', border: '1px solid #e2d8ce',
                  background: '#fcfaf7', fontSize: '13px', fontWeight: 600, color: '#3d3730', outline: 'none'
                }}
              >
                <option value="">All Levels</option>
                {availableLevels.map(lvl => (
                  <option key={lvl} value={lvl}>{lvl}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Batches Table (Row by Row) */}
          <div className="dash-card">
            <div className="dash-card-header">
              <div className="dash-card-title">
                Academy Batches ({filteredBatches.length})
              </div>
              <span className="badge badge-gold">Select a batch to inspect attendance</span>
            </div>

            {filteredBatches.length === 0 ? (
              <div className="dash-empty" style={{ padding: '40px 20px' }}>
                <div className="dash-empty-icon">📚</div>
                <div className="dash-empty-title">No Batches Found</div>
                <p className="dash-empty-sub">
                  No academy batches match your search query. Try clearing your filters.
                </p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Batch Name</th>
                      <th>Level / Category</th>
                      <th>Assigned Instructor</th>
                      <th>Class Schedule</th>
                      <th>Enrolled Students</th>
                      <th style={{ textAlign: 'right' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBatches.map(batch => {
                      const bName = getSafeName(batch.name);
                      const instructor = getBatchInstructorName(batch, tutors);
                      const scheduleStr = (batch.days && batch.days.length > 0)
                        ? batch.days.join(', ')
                        : 'Flexible';
                      const timeStr = (batch.startTime && batch.endTime)
                        ? `${batch.startTime} - ${batch.endTime}`
                        : '—';
                      const studentsCount = (batch.students || []).length;

                      return (
                        <tr
                          key={batch._id}
                          style={{ cursor: 'pointer', transition: 'background 0.2s ease' }}
                          onClick={() => { setSelectedBatch(batch); setViewMode('all'); }}
                        >
                          <td>
                            <div style={{ fontWeight: 700, color: 'var(--wine)', fontSize: '14px' }}>
                              {bName}
                            </div>
                            <div style={{ fontSize: '11px', color: '#888' }}>
                              ID: {String(batch._id).slice(-6)}
                            </div>
                          </td>
                          <td>
                            <span className="badge badge-blue">
                              {batch.level || 'Standard'}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 600, color: '#1a1a2e', fontSize: '13px' }}>
                              🎓 {instructor}
                            </div>
                          </td>
                          <td>
                            <div style={{ fontWeight: 600, fontSize: '13px', color: '#1a1a2e' }}>
                              🗓️ {scheduleStr}
                            </div>
                            <div style={{ fontSize: '12px', color: '#7a7068', marginTop: '2px' }}>
                              ⏰ {timeStr}
                            </div>
                          </td>
                          <td>
                            <span className="badge badge-gold">
                              👥 {studentsCount} {studentsCount === 1 ? 'Student' : 'Students'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              className="dash-btn-primary"
                              onClick={e => {
                                e.stopPropagation();
                                setSelectedBatch(batch);
                                setViewMode('all');
                              }}
                              style={{
                                padding: '8px 16px', borderRadius: '10px', fontSize: '12px', fontWeight: 700,
                                background: 'var(--wine)', color: '#fff', border: 'none', cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(123, 29, 58, 0.2)'
                              }}
                            >
                              View Attendance ➔
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ── STEP 2: Selected Batch Student Attendance Ledger ── */
        <div>
          {/* Breadcrumb Navigation Bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            marginBottom: '20px', flexWrap: 'wrap'
          }}>
            <button
              onClick={() => setSelectedBatch(null)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '8px 16px', borderRadius: '10px',
                border: '1px solid #7b1d3a', background: '#fff',
                color: '#7b1d3a', fontWeight: 700, fontSize: '13px',
                cursor: 'pointer', transition: 'all 0.2s ease',
                boxShadow: '0 2px 6px rgba(123, 29, 58, 0.08)'
              }}
            >
              ← All Batches
            </button>
            <span style={{ color: '#ccc', fontWeight: 700 }}>/</span>
            <span style={{ fontWeight: 800, color: '#1a1a2e', fontSize: '15px' }}>
              📚 {batchName} ({selectedBatch.level || 'Standard'})
            </span>
          </div>

          {/* Batch Identity Card with View Switcher */}
          <div style={{
            background: 'linear-gradient(135deg, #fcfaf7 0%, #f4ede4 100%)',
            borderRadius: '16px', padding: '20px 24px', marginBottom: '24px',
            border: '1px solid #e2d8ce', display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', flexWrap: 'wrap', gap: '16px'
          }}>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: '#7b1d3a', letterSpacing: '0.5px' }}>
                Batch Attendance Ledger
              </div>
              <h2 style={{ margin: '4px 0 6px', fontSize: '22px', fontWeight: 800, color: '#1a1a2e' }}>
                {batchName}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: '#5a5048' }}>
                <span>🎓 Instructor: <strong>{getBatchInstructorName(selectedBatch, tutors)}</strong></span>
                <span>🏷️ Level: <strong>{selectedBatch.level || 'Standard'}</strong></span>
                <span>🗓️ Schedule: <strong>{(selectedBatch.days || []).join(', ') || 'Flexible'} ({selectedBatch.startTime || '—'} - {selectedBatch.endTime || '—'})</strong></span>
                <span>👥 <strong>{(selectedBatch.students || []).length} Enrolled Students</strong></span>
              </div>
            </div>

            {/* Sub-view switcher pill */}
            <div style={{ display: 'flex', background: '#e8ded2', padding: '3px', borderRadius: '12px' }}>
              <button
                onClick={() => setActiveSubView('logs')}
                style={{
                  padding: '7px 16px', borderRadius: '10px', border: 'none',
                  background: activeSubView === 'logs' ? '#fff' : 'transparent',
                  color: activeSubView === 'logs' ? '#7b1d3a' : '#7a7068',
                  fontWeight: activeSubView === 'logs' ? 700 : 500, fontSize: '13px', cursor: 'pointer',
                  boxShadow: activeSubView === 'logs' ? '0 2px 6px rgba(0,0,0,0.08)' : 'none'
                }}
              >
                📋 Roll-Call Logs
              </button>
              <button
                onClick={() => setActiveSubView('matrix')}
                style={{
                  padding: '7px 16px', borderRadius: '10px', border: 'none',
                  background: activeSubView === 'matrix' ? '#fff' : 'transparent',
                  color: activeSubView === 'matrix' ? '#7b1d3a' : '#7a7068',
                  fontWeight: activeSubView === 'matrix' ? 700 : 500, fontSize: '13px', cursor: 'pointer',
                  boxShadow: activeSubView === 'matrix' ? '0 2px 6px rgba(0,0,0,0.08)' : 'none'
                }}
              >
                📅 Monthly Grid Matrix
              </button>
            </div>
          </div>

          {activeSubView === 'matrix' ? (
            <MonthlyReportTab batches={batches} students={students} defaultBatchId={selectedBatch._id} />
          ) : (
            <>
              {/* 4 Stat Cards for Selected Batch */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
              }}>
                <div style={{
                  background: '#fff', borderRadius: '14px', padding: '18px 20px',
                  border: '1px solid #e2d8ce', boxShadow: '0 4px 15px rgba(0,0,0,0.02)'
                }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#7a7068', textTransform: 'uppercase', marginBottom: '4px' }}>
                    Total Marks Recorded
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 800, color: '#1a1a2e' }}>
                    {batchStats.total}
                  </div>
                </div>

                <div style={{
                  background: '#fff', borderRadius: '14px', padding: '18px 20px',
                  border: '1px solid #e2d8ce', boxShadow: '0 4px 15px rgba(0,0,0,0.02)'
                }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#059669', textTransform: 'uppercase', marginBottom: '4px' }}>
                    Present / Verified
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 800, color: '#059669' }}>
                    {batchStats.present}
                  </div>
                </div>

                <div style={{
                  background: '#fff', borderRadius: '14px', padding: '18px 20px',
                  border: '1px solid #e2d8ce', boxShadow: '0 4px 15px rgba(0,0,0,0.02)'
                }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#e11d48', textTransform: 'uppercase', marginBottom: '4px' }}>
                    Absent / Missed
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 800, color: '#e11d48' }}>
                    {batchStats.absent}
                  </div>
                </div>

                <div style={{
                  background: '#fff', borderRadius: '14px', padding: '18px 20px',
                  border: '1px solid #e2d8ce', boxShadow: '0 4px 15px rgba(0,0,0,0.02)'
                }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#d97706', textTransform: 'uppercase', marginBottom: '4px' }}>
                    Other / Leave / Makeup
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 800, color: '#d97706' }}>
                    {batchStats.other}
                  </div>
                </div>
              </div>

              {/* Filter Toolbar & Actions */}
              <div style={{
                background: '#fff', borderRadius: '16px', padding: '16px 20px', marginBottom: '24px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f0e6da',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', background: '#f0e6da', padding: '3px', borderRadius: '12px' }}>
                    <button
                      onClick={() => setViewMode('all')}
                      style={{
                        padding: '6px 14px', borderRadius: '10px', border: 'none',
                        background: viewMode === 'all' ? '#fff' : 'transparent',
                        color: viewMode === 'all' ? '#7b1d3a' : '#7a7068',
                        fontWeight: viewMode === 'all' ? 700 : 500, fontSize: '13px', cursor: 'pointer',
                        boxShadow: viewMode === 'all' ? '0 2px 6px rgba(0,0,0,0.08)' : 'none'
                      }}
                    >
                      All Dates
                    </button>
                    <button
                      onClick={() => setViewMode('date')}
                      style={{
                        padding: '6px 14px', borderRadius: '10px', border: 'none',
                        background: viewMode === 'date' ? '#fff' : 'transparent',
                        color: viewMode === 'date' ? '#7b1d3a' : '#7a7068',
                        fontWeight: viewMode === 'date' ? 700 : 500, fontSize: '13px', cursor: 'pointer',
                        boxShadow: viewMode === 'date' ? '0 2px 6px rgba(0,0,0,0.08)' : 'none'
                      }}
                    >
                      📅 Specific Date
                    </button>
                    <button
                      onClick={() => setViewMode('month')}
                      style={{
                        padding: '6px 14px', borderRadius: '10px', border: 'none',
                        background: viewMode === 'month' ? '#fff' : 'transparent',
                        color: viewMode === 'month' ? '#7b1d3a' : '#7a7068',
                        fontWeight: viewMode === 'month' ? 700 : 500, fontSize: '13px', cursor: 'pointer',
                        boxShadow: viewMode === 'month' ? '0 2px 6px rgba(0,0,0,0.08)' : 'none'
                      }}
                    >
                      🗓️ Monthly Ledger
                    </button>
                  </div>

                  {viewMode === 'date' && (
                    <input
                      type="date"
                      value={selectedDate}
                      min={selectedBatch?.startDate ? selectedBatch.startDate.slice(0, 10) : undefined}
                      max={selectedBatch?.endDate ? selectedBatch.endDate.slice(0, 10) : undefined}
                      onChange={e => setSelectedDate(e.target.value)}
                      style={{
                        padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2d8ce',
                        fontSize: '13px', fontWeight: 600, color: '#3d3730', outline: 'none'
                      }}
                    />
                  )}

                  {viewMode === 'month' && (
                    <select
                      value={selectedMonth}
                      onChange={e => setSelectedMonth(e.target.value)}
                      style={{
                        padding: '8px 14px', borderRadius: '8px', border: '1px solid #e2d8ce',
                        background: '#fff', fontSize: '13px', fontWeight: 600, color: '#3d3730', outline: 'none',
                        minWidth: '160px', cursor: 'pointer'
                      }}
                    >
                      {batchMonths.map(m => (
                        <option key={m} value={m}>
                          {monthLabel(m)}
                        </option>
                      ))}
                    </select>
                  )}

                  <select
                    value={selectedStatus}
                    onChange={e => setSelectedStatus(e.target.value)}
                    style={{
                      padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2d8ce',
                      background: '#fcfaf7', fontSize: '13px', fontWeight: 600, color: '#3d3730', outline: 'none'
                    }}
                  >
                    <option value="">All Statuses</option>
                    <option value="Present">✅ Present</option>
                    <option value="Absent">❌ Absent</option>
                    <option value="Late">⏱️ Late</option>
                    <option value="Leave">🏖️ Leave</option>
                    <option value="Make-up">🔄 Make-up</option>
                    <option value="Holiday">🌴 Holiday</option>
                    <option value="Rescheduled">📅 Rescheduled</option>
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    onClick={handleExportCSV}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '9px 16px', borderRadius: '10px',
                      border: '1px solid #7b1d3a', background: '#fff',
                      color: '#7b1d3a', fontWeight: 700, fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    📥 Export CSV
                  </button>
                  <button
                    onClick={handlePrint}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '9px 16px', borderRadius: '10px',
                      border: 'none', background: 'var(--wine)',
                      color: '#fff', fontWeight: 700, fontSize: '13px',
                      cursor: 'pointer', boxShadow: '0 4px 12px rgba(123, 29, 58, 0.2)'
                    }}
                  >
                    🖨️ Print Report
                  </button>
                </div>
              </div>

              {/* Student Attendance Records Table */}
              <div className="dash-card">
                <div className="dash-card-header">
                  <div className="dash-card-title">
                    Student Attendance Records for {batchName}
                  </div>
                  <span className="badge badge-gold">
                    {filteredLogs.length} {filteredLogs.length === 1 ? 'Record' : 'Records'}
                  </span>
                </div>

                {loading ? (
                  <div className="dash-loading">⏳ Loading student attendance records...</div>
                ) : filteredLogs.length === 0 ? (
                  <div className="dash-empty" style={{ padding: '40px 20px' }}>
                    <div className="dash-empty-icon">📅</div>
                    <div className="dash-empty-title">No Student Attendance Records Found</div>
                    <p className="dash-empty-sub" style={{ maxWidth: '420px', margin: '8px auto' }}>
                      No attendance records match your filter for <strong>{batchName}</strong>. Once roll call is conducted in the student or tutor portal, student entries will appear here.
                    </p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <table className="dash-table">
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th>Attendance Status</th>
                          <th>Class Date</th>
                          <th>Time Marked</th>
                          <th>Marked By</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLogs.map(log => {
                          const { name, rollNo } = getStudentDisplay(log, students);
                          const isPresent = (log.status || '').toLowerCase() === 'present';
                          const isAbsent = (log.status || '').toLowerCase() === 'absent';
                          const markedByName = (typeof log.markedBy === 'object' && log.markedBy?.name)
                            ? tField(log.markedBy.name)
                            : 'Instructor';

                          return (
                            <tr key={log._id}>
                              <td>
                                <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: '14px' }}>
                                  {name}
                                </div>
                                {rollNo ? (
                                  <div style={{ fontSize: '11px', color: '#7a7068', marginTop: '2px' }}>
                                    ID: {rollNo}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>—</div>
                                )}
                              </td>
                              <td>
                                <span className={`badge ${STATUS_BADGE(log.status)}`}>
                                  {isPresent ? '✅ Present' : (isAbsent ? '❌ Absent' : log.status)}
                                </span>
                              </td>
                              <td style={{ fontWeight: 600, color: '#1a1a2e', fontSize: '13px' }}>
                                {log.date ? FMT_DATE(log.date) : (log.createdAt ? FMT_DATE(log.createdAt) : '—')}
                              </td>
                              <td style={{ fontSize: '13px', color: '#5a5048' }}>
                                {log.createdAt ? FMT_TIME(log.createdAt) : '—'}
                              </td>
                              <td style={{ fontSize: '13px', color: '#5a5048' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                  🎓 {markedByName}
                                </span>
                              </td>
                              <td className="text-sm" style={{ color: log.note ? '#3d3730' : '#aaa' }}>
                                {log.note || '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════
   Main AttendanceCenter Page
════════════════════════════════════════ */
export default function AttendanceCenter({ initialTab = 'students' }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const [loading, setLoading]   = useState(true);
  const [batches, setBatches]   = useState([]);
  const [students, setStudents] = useState([]);
  const [tutors, setTutors]     = useState([]);
  const [stats, setStats]       = useState({ studentsPresent: 0, studentsAbsent: 0, tutorsActive: 0 });

  const loadBaseData = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, tRes, sRes, stRes] = await Promise.all([
        batchesAPI.getAll(),
        tutorsAPI.getAll(),
        attendanceAPI.getSummary(new Date().toISOString().split('T')[0]),
        studentsAPI.getAll(),
      ]);
      setBatches(bRes.data || []);
      setTutors(tRes.data || []);
      setStats(sRes.data || { studentsPresent: 0, studentsAbsent: 0, tutorsActive: 0 });
      setStudents(stRes.data || []);
    } catch (e) {
      console.error('Error loading attendance base data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  return (
    <Layout title={activeTab === 'tutors' ? t('attendanceUI.tutorAttendanceTitle', 'Tutor Attendance Ledger') : t('attendanceUI.pageTitle', 'Student Attendance Center')}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#7a7068', fontSize: '15px', fontWeight: 600 }}>
          ⏳ Loading academy attendance center...
        </div>
      ) : activeTab === 'tutors' ? (
        <TutorAttendanceHub batches={batches} tutors={tutors} />
      ) : (
        <StudentAttendanceHub batches={batches} tutors={tutors} students={students} dailyStats={stats} />
      )}
    </Layout>
  );
}
